import type { ControlMode } from '../core/ControlSettings';
import * as THREE from 'three';
import type RAPIER from '@dimforge/rapier3d-compat';
import { buildStadium, buildTrophy, type Stadium } from '../assets/CourtBuilder';
import { VfxSystem } from '../assets/Vfx';
import { InputController } from '../core/InputController';
import { Loop } from '../core/Loop';
import { createRenderer, resizeRenderer } from '../core/Renderer';
import { Ball } from '../entities/Ball';
import type { ItemBox } from '../entities/ItemBox';
import { magnetActive, PlayerEntity, type ItemType, type PlayerDef } from '../entities/PlayerEntity';
import { AiSystem, type AiContext } from '../systems/AiSystem';
import { AudioSystem } from '../systems/AudioSystem';
import { CameraDirector } from '../systems/CameraDirector';
import { DebugTools, type DebugTuning } from '../systems/DebugTools';
import { Hud } from '../systems/Hud';
import { PhysicsWorld } from '../systems/PhysicsWorld';
import { createSeededRandom } from '../utils/random';
import { BALL, COURT, FORMATIONS, GOAL, GOALKEEPER_AREA, ITEMS, MATCH, PLAYER, QUALITY, SCORING } from './constants';
import { ATTACKS, type AttackStyle } from './combat';
import { WEAPONS, WEAPON_TYPES, SHOTGUN_KILL_RANGE, ROCKET, isHeavyWeapon, type WeaponType } from './weapons';
import { ArenaWeapons } from '../systems/ArenaWeapons';
import { RocketSystem } from '../systems/RocketSystem';
import { OptionsPanel } from '../systems/OptionsPanel';
import { getAttackDamage, getCombatTuning, getSpecialDamage, getWeaponDamage, type CombatTuning } from './CombatTuning';
import { OnlineSession, type OnlineSnapshot } from '../systems/OnlineSession';
import type { ControlIntent } from '../core/InputController';
import type { BannerTone } from '../systems/Hud';

type Phase = 'title' | 'countdown' | 'play' | 'goal' | 'paused' | 'complete';

interface Bomb {
  ownerId: string;
  body: RAPIER.RigidBody;
  mesh: THREE.Group;
  core: THREE.MeshStandardMaterial;
  fuse: number;
}

function buildBombVisual(): { mesh: THREE.Group; core: THREE.MeshStandardMaterial } {
  const mesh = new THREE.Group();
  const core = new THREE.MeshStandardMaterial({ color: '#22242c', roughness: 0.35, emissive: '#000000', emissiveIntensity: 0 });
  const sphere = new THREE.Mesh(new THREE.SphereGeometry(0.17, 14, 12), core);
  sphere.castShadow = true;
  mesh.add(sphere);
  const fuse = new THREE.Mesh(new THREE.CylinderGeometry(0.014, 0.014, 0.16, 6), new THREE.MeshStandardMaterial({ color: '#c9a86b', roughness: 0.8 }));
  fuse.position.set(0.05, 0.16, 0);
  fuse.rotation.z = -0.5;
  mesh.add(fuse);
  return { mesh, core };
}

const TEAM_COLORS = { A: '#3f8cff', B: '#ff5349' } as const;
const TEAM_LABELS = { A: 'AZUL', B: 'ROJO' } as const;
const P1_ID = 'a-1';
const P2_ID = 'b-1';
const AUTO_FOCUS_MARGIN = 0.55;
const MANUAL_FOCUS_GRACE = 1.1;

function playerDefs(): PlayerDef[] {
  const base = {
    shorts: '#1d3f7a',
    socks: '#2f6fd6',
    isGoalkeeper: false,
  };
  const baseB = {
    shorts: '#7a1d1d',
    socks: '#d63a34',
    isGoalkeeper: false,
  };
  return [
    {
      id: 'a-gk',
      name: 'PULPO',
      team: 'A',
      isGoalkeeper: true,
      spec: {
        ...base,
        isGoalkeeper: true,
        id: 0,
        skin: '#e8b48c',
        hair: '#2c2016',
        hairStyle: 'spiky',
        jersey: '#2f6fd6',
        boots: '#ffd23f',
        gloves: '#ffd23f',
        number: 1,
        numberColor: '#ffffff',
        eyes: 'fierce',
      },
    },
    {
      id: 'a-1',
      name: 'MATEO',
      team: 'A',
      isGoalkeeper: false,
      spec: {
        ...base,
        id: 1,
        skin: '#c98d5f',
        hair: '#141414',
        hairStyle: 'mop',
        jersey: '#2f6fd6',
        boots: '#ffd23f',
        gloves: '#f4f6f8',
        number: 7,
        numberColor: '#ffffff',
        eyes: 'focused',
      },
    },
    {
      id: 'a-2',
      name: 'NICO',
      team: 'A',
      isGoalkeeper: false,
      spec: {
        ...base,
        id: 2,
        skin: '#8d5a3b',
        hair: '#241a12',
        hairStyle: 'ponytail',
        jersey: '#2f6fd6',
        boots: '#ffd23f',
        gloves: '#f4f6f8',
        number: 10,
        numberColor: '#ffffff',
        eyes: 'happy',
      },
    },
    {
      id: 'b-gk',
      name: 'TIBURON',
      team: 'B',
      isGoalkeeper: true,
      spec: {
        ...baseB,
        isGoalkeeper: true,
        id: 3,
        skin: '#f0c9a0',
        hair: '#3d2c1a',
        hairStyle: 'mop',
        jersey: '#d63a34',
        boots: '#f4f6f8',
        gloves: '#57e0ff',
        number: 1,
        numberColor: '#ffffff',
        eyes: 'fierce',
      },
    },
    {
      id: 'b-1',
      name: 'TIAGO',
      team: 'B',
      isGoalkeeper: false,
      spec: {
        ...baseB,
        id: 4,
        skin: '#b87848',
        hair: '#101010',
        hairStyle: 'bald',
        jersey: '#d63a34',
        boots: '#f4f6f8',
        gloves: '#f4f6f8',
        number: 9,
        numberColor: '#ffffff',
        eyes: 'fierce',
      },
    },
    {
      id: 'b-2',
      name: 'DANTE',
      team: 'B',
      isGoalkeeper: false,
      spec: {
        ...baseB,
        id: 5,
        skin: '#d49a73',
        hair: '#c99534',
        hairStyle: 'spiky',
        jersey: '#d63a34',
        boots: '#f4f6f8',
        gloves: '#f4f6f8',
        number: 4,
        numberColor: '#ffffff',
        eyes: 'happy',
      },
    },
  ];
}

export class Game {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera: THREE.PerspectiveCamera;
  private readonly director: CameraDirector;
  private readonly input: InputController;
  private readonly audio = new AudioSystem();
  private readonly hud = new Hud();
  private readonly physics = new PhysicsWorld();
  private readonly vfx = new VfxSystem();
  private readonly players: PlayerEntity[] = [];
  private readonly ai: AiSystem;
  private readonly weapons: ArenaWeapons;
  private readonly rockets: RocketSystem;
  private readonly options: OptionsPanel;
  private readonly menuEvents = new AbortController();
  private optionsPausedMatch = false;
  private readonly ball: Ball;
  private readonly boxes: ItemBox[] = [];
  private readonly bombs: Bomb[] = [];
  private readonly stadium: Stadium;
  private readonly trophy: THREE.Group;
  private readonly sun: THREE.DirectionalLight;

  private readonly debugTools: DebugTools;
  private readonly tuning: DebugTuning = { exposure: 1.05, maxDpr: QUALITY.maxDpr, aiSkill: 0.42 };
  private readonly loop: Loop;

  private phase: Phase = 'title';
  private phaseBeforePause: 'play' | 'countdown' = 'play';
  private phaseTimer = 0;
  private scoreA = 0;
  private scoreB = 0;
  private matchTime = 0;
  private golden = false;
  private pendingEnd = false;
  private scorerHot: THREE.Vector3 | null = null;
  private netFlash = 0;
  private crowdExcite = 0;
  private hitstop = 0;
  private frame = 0;
  private anyInput = false;
  private pausedForScreenshot = false;
  private reducedMotion = false;
  private titleSway = 0;
  private trailTimer = 0;
  private statusTimer = 0;
  private bannerTimer = 0;
  private combatChain = 0;
  private chainTimer = 0;
  private combatHits = 0;
  private lastAttack: AttackStyle | null = null;
  private combatTest = false;
  private chargeAudioId: string | null = null;
  private readonly humanPlayerIds = [P1_ID, P2_ID];
  private readonly humanFocusGrace = [0, 0];
  private readonly focusBallScratch = new THREE.Vector3();
  private localPlayerCount = 1;
  private setupStage: 'count' | 'sides' = 'count';
  private readonly previousHumanSprint = [false, false];
  private readonly online: OnlineSession;
  private onlineSnapshot: OnlineSnapshot | null = null;
  private snapshotAge = 0;
  private lastNetworkSend = 0;
  private lastRemoteInput = 0;
  private remoteIntent: ControlIntent = { moveX: 0, moveZ: 0, sprint: false, shootHeld: false, shootPressed: false, shootReleased: false, passPressed: false, punchPressed: false, active: false };
  private readonly networkAudio: OnlineSnapshot['audio'] = [];
  private readonly replicaBombs: ReturnType<typeof buildBombVisual>[] = [];

  private readonly gkThrown = new Set<string>();
  private readonly bombPending = new Set<string>();
  private explosiveBallTimer = 0;
  private readonly crateRadius = 0.55;
  private readonly separationScratch = new THREE.Vector3();
  private readonly passButton = document.querySelector<HTMLButtonElement>('#btn-pass')!;

  private rng = createSeededRandom(7);
  private qualityWindow = 0;
  private qualityFrames = 0;
  private qualityStableTime = 0;
  private qualityProbeFrom: number | null = null;
  private qualityCeiling: number = QUALITY.maxDpr;
  private fpsFrames = 0;
  private fpsSampleTime = performance.now();

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.renderer = createRenderer(canvas);
    this.renderer.toneMappingExposure = this.tuning.exposure;
    this.renderer.shadowMap.enabled = true;

    this.camera = new THREE.PerspectiveCamera(46, 1, 0.1, 220);
    this.director = new CameraDirector(this.camera);

    const stick = this.getElement('#touch-stick');
    const knob = this.getElement('#touch-knob');
    this.input = new InputController(stick, knob, {
      pass: this.getElement('#btn-pass'),
      shoot: this.getElement('#btn-shoot'),
      punch: this.getElement('#btn-punch'),
      sprint: this.getElement('#btn-sprint'),
    }, {
      root: this.getElement('#touch-player-2'),
      stick: this.getElement('#touch-stick-2'),
      knob: this.getElement('#touch-knob-2'),
      buttons: {
        pass: this.getElement('#btn-pass-2'),
        shoot: this.getElement('#btn-shoot-2'),
        punch: this.getElement('#btn-punch-2'),
        sprint: this.getElement('#btn-sprint-2'),
      },
    });

    this.stadium = buildStadium();
    this.scene.add(this.stadium.group);
    this.scene.add(this.vfx.group);

    this.trophy = buildTrophy();
    this.trophy.position.set(0.8, 0, 0);
    this.scene.add(this.trophy);

    const hemisphere = new THREE.HemisphereLight('#e8f1ff', '#3a3226', 1.05);
    this.scene.add(hemisphere);
    this.sun = new THREE.DirectionalLight('#fff3d6', 1.9);
    this.sun.position.set(-14, 24, 12);
    this.sun.castShadow = true;
    this.sun.shadow.camera.near = 2;
    this.sun.shadow.camera.far = 70;
    this.sun.shadow.camera.left = -24;
    this.sun.shadow.camera.right = 24;
    this.sun.shadow.camera.top = 20;
    this.sun.shadow.camera.bottom = -20;
    this.scene.add(this.sun);

    this.buildTeams();

    this.ball = new Ball(this.physics);
    this.ball.group.visible = false;
    this.scene.add(this.ball.group);

    this.ai = new AiSystem(this.players, () => this.rng());
    this.weapons = new ArenaWeapons(this.players, () => this.rng(), {
      onPickup: (player, type) => {
        this.audio.pickup();
        this.vfx.spawnImpact(new THREE.Vector3(player.position.x, 0.85 + player.airborneY, player.position.z), WEAPONS[type].color, 18);
        this.setStatus(`${player.name} recogió ${WEAPONS[type].name} · L ${type === 'shotgun' || type === 'bazooka' ? 'dispara' : 'golpea'} · J arroja`);
      },
      onThrow: (player, type) => {
        this.audio.weaponThrow(type);
        this.setStatus(`${player.name} arrojó ${WEAPONS[type].name}`);
      },
      onHit: (attacker, target, type, direction, broken, thrown) => this.applyWeaponHit(attacker, target, type, direction, broken, thrown),
      onEnvironment: (position, type, broken) => {
        this.audio.weaponImpact(type, broken);
        this.vfx.weaponImpact(position, type, broken);
      },
    });
    this.scene.add(this.weapons.group);
    this.rockets = new RocketSystem(this.players, {
      trail: position => this.vfx.rocketTrail(position),
      impact: (position, target, ownerId) => this.explodeRocket(position, target, ownerId),
    });
    this.scene.add(this.rockets.group);
    this.options = new OptionsPanel(this.vfx, this.audio, (open) => {
      this.input.clear();
      if (open) {
        this.optionsPausedMatch = this.phase === 'play' || this.phase === 'countdown';
        if (this.optionsPausedMatch) this.togglePause();
      } else {
        if (this.optionsPausedMatch && this.phase === 'paused') this.togglePause();
        this.optionsPausedMatch = false;
      }
    }, (visible) => this.hud.setFpsVisible(visible), (settings) => { this.input.setControlSettings(settings); this.input.setLocalPlayerCount(this.online?.started ? this.online.role === 'spectator' ? 0 : 1 : this.localPlayerCount); }, (settings) => this.applyCombatTuning(settings));

    this.online = new OnlineSession({
      start: role => this.startOnlineMatch(role),
      snapshot: state => this.receiveOnlineSnapshot(state),
      input: input => {
        this.lastRemoteInput = performance.now();
        this.remoteIntent = { ...input, punchPressed: input.punchPressed || this.remoteIntent.punchPressed,
          passPressed: input.passPressed || this.remoteIntent.passPressed, shootPressed: input.shootPressed || this.remoteIntent.shootPressed,
          shootReleased: input.shootReleased || this.remoteIntent.shootReleased };
      },
      pause: () => { if (this.online.role === 'host') this.togglePause(); },
      close: reason => {
        this.replicaBombs.forEach(bomb => { bomb.mesh.visible = false; });
        this.onlineSnapshot = null;
        this.localPlayerCount = 1;
        this.input.setLocalPlayerCount(1);
        this.openMatchSetup();
        this.showMenuDetails(null);
        this.hud.setStatus(reason);
      },
      promote: state => this.promoteOnlineMatch(state),
      resume: (role, state) => {
        this.startOnlineMatch(role);
        if (state) this.adoptOnlineState(state);
      },
      networkLost: (role, reason) => {
        if (this.onlineSnapshot) this.adoptOnlineState(this.onlineSnapshot);
        this.localPlayerCount = 1;
        this.humanPlayerIds[0] = role === 'guest' ? P2_ID : P1_ID;
        this.input.setLocalPlayerCount(1);
        this.onlineSnapshot = null;
        this.hud.setSpectator(false);
        this.getElement('#combat-hud').classList.remove('hidden');
        this.getElement('#online-match-actions').classList.add('hidden');
        this.getElement('#online-back-match').classList.add('hidden');
        this.updateControl();
        this.hud.setStatus(`${reason} La partida continúa contra la CPU.`);
      },
    });
    this.audio.onNetworkEvent = (name, args) => {
      if (this.online.started && this.online.role === 'host' && this.networkAudio.length < 32) this.networkAudio.push({ name, args });
    };

    this.debugTools = new DebugTools(this.tuning, (key) => {
      if (key === 'exposure') this.renderer.toneMappingExposure = this.tuning.exposure;
      if (key === 'maxDpr') resizeRenderer(this.renderer, this.camera, this.tuning.maxDpr);
      if (key === 'aiSkill') this.ai.setSkill(this.tuning.aiSkill);
    });
    this.ai.setSkill(this.tuning.aiSkill);

    this.loop = new Loop(
      (delta, elapsed) => { this.update(delta, elapsed); this.updateNetwork(); },
      delta => this.render(delta),
    );

    window.addEventListener('keydown', this.onAnyInput);
    window.addEventListener('pointerdown', this.onAnyInput);
    const menuOptions = { signal: this.menuEvents.signal };
    document.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('button') : null;
      if (button && !button.disabled && button.closest('#screen-title, #screen-end, #options-dialog, #editor-dialog')) this.audio.menuClick();
    }, { ...menuOptions, capture: true });
    this.getElement('#menu-quick-play').addEventListener('click', () => this.openMatchSetup(), menuOptions);
    this.getElement('#setup-next').addEventListener('click', () => this.showSetupStage('sides'), menuOptions);
    this.getElement('#setup-previous').addEventListener('click', () => this.showSetupStage('count'), menuOptions);
    this.getElement('#setup-play').addEventListener('click', () => this.confirmMatchSetup(), menuOptions);
    this.getElement('#menu-setup-content').addEventListener('change', () => this.refreshMatchSetup(), menuOptions);
    document.querySelectorAll<HTMLButtonElement>('.new-match').forEach(button => button.addEventListener('click', () => this.openMatchSetup(), menuOptions));
    this.getElement('#menu-options').addEventListener('click', () => this.getElement('#btn-options').click(), menuOptions);
    this.getElement('#menu-controls').addEventListener('click', () => this.showMenuDetails('controls'), menuOptions);
    this.getElement('#menu-about').addEventListener('click', () => this.showMenuDetails('about'), menuOptions);
    this.getElement('#menu-support').addEventListener('click', () => this.showMenuDetails('support'), menuOptions);
    this.getElement('#menu-back').addEventListener('click', () => this.showMenuDetails(null), menuOptions);
    this.getElement('#btn-pause').addEventListener('click', this.onPauseClick);
    this.getElement('#btn-sound').addEventListener('click', this.onSoundClick);
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.director.setReducedMotion(this.reducedMotion);

    this.applyQuality();
    this.director.snapTo(new THREE.Vector3(0, 0, 0), this.isPortrait());
    this.hud.setScore(0, 0);
    this.hud.setTimer(MATCH.duration, false);
    this.installTestHooks();
    if (import.meta.env.DEV) this.publishDiagnostics();
    void this.audio.startMenuMusic();
  }

  private readonly onAnyInput = (event: Event) => {
    if (this.options?.isOpen) return;
    if (this.phase === 'title') return;
    if (event.target instanceof Element && event.target.closest('#match-controls, #online-dialog, #online-match-actions, input, textarea, select')) return;
    if (event instanceof KeyboardEvent && ['KeyM', 'KeyP', 'Escape'].includes(event.code)) return;
    this.anyInput = true;
  };

  private showMenuDetails(panel: 'controls' | 'about' | 'support' | 'setup' | null): void {
    this.getElement('#screen-title').classList.toggle('setup-open', panel === 'setup');
    this.getElement('#menu-setup-content').classList.toggle('hidden', panel !== 'setup');
    this.getElement('#menu-home').classList.toggle('hidden', panel !== null);
    this.getElement('#menu-details').classList.toggle('hidden', panel === null);
    this.getElement('#menu-controls-content').classList.toggle('hidden', panel !== 'controls');
    this.getElement('#menu-about-content').classList.toggle('hidden', panel !== 'about');
    this.getElement('#menu-support-content').classList.toggle('hidden', panel !== 'support');
  }

  private openMatchSetup(): void {
    if (this.online.started) this.online.leave();
    this.phase = 'title';
    this.audio.stopBackground();
    void this.audio.startMenuMusic();
    this.audio.setPaused(false);
    this.hud.showTitle(true);
    this.hud.showPause(false);
    this.hud.hideEnd();
    this.hud.hideBanner();
    this.input.clear();
    this.options.controlSettings.forEach((control, slot) => {
      (this.getElement(`#setup-control-${slot}`) as HTMLSelectElement).value = control.mode;
    });
    this.showMenuDetails('setup');
    this.showSetupStage('count');
  }

  private setupCount(): number {
    const value = (this.getElement('#setup-count') as HTMLSelectElement).value;
    return value === '0' ? 0 : value === '2' ? 2 : 1;
  }

  private showSetupStage(stage: 'count' | 'sides'): void {
    this.setupStage = stage;
    this.getElement('#setup-count-step').classList.toggle('hidden', stage !== 'count');
    this.getElement('#setup-sides-step').classList.toggle('hidden', stage !== 'sides');
    this.refreshMatchSetup();
    this.getElement(stage === 'count' ? '#setup-count' : this.setupCount() ? '#setup-team-0' : '#setup-play').focus();
  }

  private refreshMatchSetup(): void {
    const count = this.setupCount();
    for (const slot of [0, 1]) this.getElement(`#setup-player-${slot}`).classList.toggle('hidden', slot >= count);
    this.getElement('#setup-spectator').classList.toggle('hidden', count !== 0);
    this.getElement('#setup-play').textContent = count ? 'JUGAR' : 'VER PARTIDO';
    const labels = Array.from({ length: count }, (_, slot) => {
      const team = (this.getElement(`#setup-team-${slot}`) as HTMLSelectElement).value;
      const control = this.getElement(`#setup-control-${slot}`) as HTMLSelectElement;
      return `Jugador ${slot + 1}: ${team === 'B' ? 'ROJO' : 'AZUL'} · ${control.selectedOptions[0].textContent}`;
    });
    this.getElement('#setup-summary').textContent = count ? labels.join(' / ') : 'AZUL CPU contra ROJO CPU · Sin jugadores locales';
  }

  private confirmMatchSetup(): void {
    this.localPlayerCount = this.setupCount();
    const settings = this.options.controlSettings;
    for (const slot of [0, 1]) {
      if (slot >= this.localPlayerCount) continue;
      settings[slot].mode = (this.getElement(`#setup-control-${slot}`) as HTMLSelectElement).value as ControlMode;
      const team = (this.getElement(`#setup-team-${slot}`) as HTMLSelectElement).value === 'B' ? 'B' : 'A';
      this.humanPlayerIds[slot] = this.players.find(player => player.team === team && (!slot || player.id !== this.humanPlayerIds[0]) && !player.isGoalkeeper)!.id;
    }
    this.options.setPlayerControls(settings);
    this.previousHumanSprint.fill(false);
    this.humanFocusGrace.fill(0);
    this.input.clear();
    this.hud.setSpectator(this.localPlayerCount === 0);
    this.getElement('#combat-hud').classList.toggle('hidden', this.localPlayerCount === 0);
    this.updateControl();
    for (const player of this.players) player.setRingVisible(player.controlledBy !== 'ai');
    this.startMatch();
  }

  private readonly onPauseClick = () => this.togglePause();
  private readonly onSoundClick = () => {
    this.audio.setMuted(!this.audio.getMuted());
    const button = this.getElement('#btn-sound');
    button.textContent = this.audio.getMuted() ? 'MUDO' : 'SONIDO';
    button.setAttribute('aria-pressed', String(this.audio.getMuted()));
    button.setAttribute('aria-label', this.audio.getMuted() ? 'Activar sonido' : 'Silenciar sonido');
  };

  private togglePause(): void {
    if (this.online.started && this.online.role === 'spectator') return;
    if (this.online.started && this.online.role === 'guest') { this.online.send({ type: 'pause' }); return; }
    if (this.phase === 'paused') {
      this.phase = this.phaseBeforePause;
      this.hud.showPause(false);
      this.audio.setPaused(false);
    } else if (this.phase === 'play' || this.phase === 'countdown') {
      this.phaseBeforePause = this.phase;
      this.phase = 'paused';
      this.hud.showPause(true);
      this.audio.setPaused(true);
    }
    this.getElement('#btn-pause').setAttribute('aria-label', this.phase === 'paused' ? 'Reanudar partido' : 'Pausar partido');
  }

  start(): Promise<void> {
    return this.bootstrap();
  }

  private async bootstrap(): Promise<void> {
    await this.physics.init();
    this.ball.spawn(new THREE.Vector3(0, BALL.radius + 0.02, 0));
    this.physics.onBallContact = (tag, speed) => this.onBallContact(tag, speed);
    this.resetKickoff();
    for (const player of this.players) {
      player.update(0.016, 0, false);
    }
    // Compile hidden weapons and effects before their first appearance in play.
    // Keep shader warm-up from blocking the match forever under GPU contention.
    await Promise.race([this.renderer.compileAsync(this.scene, this.camera), new Promise(resolve => window.setTimeout(resolve, 1500))]);
    this.render();
    this.loop.start();
  }

  dispose(): void {
    this.online.leave();
    this.loop.stop();
    window.removeEventListener('keydown', this.onAnyInput);
    window.removeEventListener('pointerdown', this.onAnyInput);
    this.menuEvents.abort();
    this.input.dispose();
    this.options.dispose();
    this.weapons.dispose();
    this.rockets.dispose();
    this.getElement('#btn-pause').removeEventListener('click', this.onPauseClick);
    this.getElement('#btn-sound').removeEventListener('click', this.onSoundClick);
    this.audio.dispose();
    this.debugTools.dispose();
    for (const box of this.boxes) box.dispose();
    for (const player of this.players) player.dispose();
    for (const bomb of this.bombs) {
      this.physics.removeBody(bomb.body);
      this.scene.remove(bomb.mesh);
    }
    this.physics.dispose();
    this.vfx.dispose();
    this.renderer.dispose();
    this.renderer.forceContextLoss();
    window.__THREE_GAME_DIAGNOSTICS__ = undefined;
    window.__THREE_GAME_TEST_HOOKS__ = undefined;
  }

  private applyQuality(): void {
    const mobile = this.isPortrait() || (navigator.maxTouchPoints ?? 0) > 0;
    const size = mobile ? QUALITY.mobileShadowMapSize : QUALITY.shadowMapSize;
    this.sun.shadow.mapSize.set(size, size);
    this.qualityCeiling = mobile ? QUALITY.mobileMaxDpr : QUALITY.maxDpr;
    this.tuning.maxDpr = this.qualityCeiling;
    resizeRenderer(this.renderer, this.camera, this.tuning.maxDpr);
  }

  private adaptRenderResolution(delta: number): void {
    this.qualityWindow += delta;
    this.qualityFrames += 1;
    if (this.qualityWindow < 1) return;
    const frameTime = this.qualityWindow / this.qualityFrames;
    let next = this.tuning.maxDpr;
    if (frameTime > 1 / 59.5) {
      if (this.qualityProbeFrom != null) {
        this.qualityCeiling = Math.min(this.qualityCeiling, this.qualityProbeFrom);
        this.qualityProbeFrom = null;
      }
      this.qualityStableTime = 0;
      next = Math.max(1, next - 0.25);
    } else if (frameTime < 1 / 59.8) {
      this.qualityStableTime += this.qualityWindow;
      if (this.qualityStableTime >= 4) {
        this.qualityStableTime = 0;
        next = Math.min(this.qualityCeiling, next + 0.25);
        if (next > this.tuning.maxDpr) this.qualityProbeFrom = this.tuning.maxDpr;
      }
    } else {
      this.qualityStableTime = 0;
    }
    this.qualityWindow = 0;
    this.qualityFrames = 0;
    if (next === this.tuning.maxDpr) return;
    this.tuning.maxDpr = next;
    resizeRenderer(this.renderer, this.camera, next);
  }

  private updateFps(): void {
    this.fpsFrames += 1;
    const now = performance.now();
    const elapsed = now - this.fpsSampleTime;
    if (elapsed < 500) return;
    this.hud.setFps(Math.round((this.fpsFrames * 1000) / elapsed));
    this.fpsFrames = 0;
    this.fpsSampleTime = now;
  }

  private isPortrait(): boolean {
    return window.innerWidth < window.innerHeight;
  }

  private buildTeams(): void {
    const defs = playerDefs();
    for (const def of defs) {
      const ringColor = def.team === 'A' ? TEAM_COLORS.A : TEAM_COLORS.B;
      const player = new PlayerEntity(def, ringColor, () => this.rng());
      player.setMaxHp(getCombatTuning().characterHp[player.id as keyof CombatTuning['characterHp']]);
      player.events.onKickRequest = (owner, direction, charge, aimAtGoal) => this.doKick(owner, direction, charge, aimAtGoal);
      player.events.onPassRequest = (owner) => this.doPass(owner);
      player.events.onItemUse = (owner) => this.useItem(owner);
      player.events.onSlideStart = () => {
        this.audio.slide();
      };
      player.events.onStumble = (fallen) => {
        this.weapons.dropFrom(fallen);
        this.audio.down();
        this.vfx.spawnDust(fallen.position, 10);
        if (this.ball.ownerId === fallen.id) {
          this.ball.ownerId = null;
          this.ball.state = 'free';
          this.ball.pickupLockId = fallen.id;
          this.ball.pickupLockTimer = 0.5;
          this.ball.setVelocity(new THREE.Vector3(fallen.facing.x * 3, 1.5, fallen.facing.z * 3));
        }
        this.setStatus(`${fallen.name} tropieza por sus lesiones`);
      };
      player.events.onEliminated = (fallen) => {
        this.weapons.dropFrom(fallen);
        if (this.ball.ownerId === fallen.id) {
          this.ball.ownerId = null;
          this.ball.state = 'free';
          this.ball.setVelocity(new THREE.Vector3(0, 3, 0));
        }
        const origin = new THREE.Vector3(fallen.position.x, 0.55, fallen.position.z);
        this.vfx.spawnExplosion(origin);
        this.vfx.spawnImpact(origin, TEAM_COLORS[fallen.team], 30);
        this.vfx.spawnImpact(origin, '#b52b48', 24);
        this.audio.explosion();
        this.audio.bark('fatality');
        this.cameraShake(0.65);
        this.hud.impactFlash('hard');
        this.hud.showBanner('¡EXPLOTÓ!', `${fallen.name} VUELVE TRAS UN GOL`, 'info');
        this.bannerTimer = 1.6;
        for (const slot of [0, 1] as const) {
          if (slot >= this.localPlayerCount || this.humanPlayerIds[slot] !== fallen.id) continue;
          const available = this.players.filter(mate => mate.team === fallen.team && !mate.eliminated &&
            !this.humanPlayerIds.some((id, other) => other !== slot && other < this.localPlayerCount && id === mate.id));
          const next = available.find(mate => mate.canAct) ?? available[0];
          if (next) this.humanPlayerIds[slot] = next.id;
        }
      };
      player.events.onAttackStart = (attacker) => {
        const attack = ATTACKS[attacker.attackStyle];
        let nearest: PlayerEntity | undefined;
        let distance = attack.range + 0.65;
        for (const other of this.players) {
          if (other.team === attacker.team || other.isDown) continue;
          const offset = other.position.clone().sub(attacker.position);
          const length = offset.length();
          const directed = Math.hypot(attacker.intent?.moveX ?? 0, attacker.intent?.moveZ ?? 0) > 0.1;
          if (length < distance && (!directed || offset.normalize().dot(attacker.facing) > -0.2)) {
            nearest = other;
            distance = length;
          }
        }
        if (nearest) attacker.facing.copy(nearest.position).sub(attacker.position).setY(0).normalize();
        this.audio.combatSwing(attacker.attackStyle);
        this.vfx.attackSweep(attacker.position, attacker.facing, attacker.attackStyle);
      };
      player.events.onWeaponSwingStart = (attacker) => {
        if (!attacker.weaponType) return;
        if (attacker.weaponType === 'shotgun') { this.fireShotgun(attacker); return; }
        if (attacker.weaponType === 'bazooka') { this.fireBazooka(attacker); return; }
        this.audio.weaponSwing(attacker.weaponType);
        const nearest = this.players.filter(other => other.team !== attacker.team && !other.isDown)
          .sort((a, b) => a.position.distanceToSquared(attacker.position) - b.position.distanceToSquared(attacker.position))[0];
        if (nearest && nearest.position.distanceTo(attacker.position) < WEAPONS[attacker.weaponType].range + 0.4) {
          attacker.facing.copy(nearest.position).sub(attacker.position).setY(0).normalize();
        }
      };
      player.events.onWeaponThrowRequest = (player) => this.weapons.throwFrom(player);
      player.events.onLanding = (landed) => {
        this.audio.combatLanding();
        this.vfx.spawnDust(landed.position, 24);
        this.vfx.shockwave(landed.position, '#fff1c9', landed.hitReaction === 'flatten' ? 3 : 1.8, 0.35);
        this.cameraShake(0.18);
      };
      this.players.push(player);
      this.scene.add(player.group);
    }
  }

  private applyCombatTuning(settings: CombatTuning): void {
    this.audio.setVolumes(settings.sfxVolume / 100, settings.musicVolume / 100);
    for (const player of this.players) player.setMaxHp(settings.characterHp[player.id as keyof CombatTuning['characterHp']]);
  }

  private player(id: string): PlayerEntity {
    const found = this.players.find((p) => p.id === id);
    if (!found) throw new Error(`Unknown player ${id}`);
    return found;
  }

  private human0Player(): PlayerEntity {
    return this.player(this.humanPlayerIds[0]);
  }

  private resetKickoff(restoreHealth = false): void {
    this.hitstop = 0;
    this.weapons.reset();
    this.rockets.clear();
    this.ball.reset(new THREE.Vector3(0, BALL.radius + 0.02, 0));
    this.ball.group.visible = true;
    this.gkThrown.clear();
    this.bombPending.clear();
    for (const bomb of this.bombs) {
      this.physics.removeBody(bomb.body);
      this.scene.remove(bomb.mesh);
    }
    this.bombs.length = 0;
    const teamA = this.players.filter((p) => p.team === 'A');
    const teamB = this.players.filter((p) => p.team === 'B');
    teamA.forEach((p, index) => {
      if (!restoreHealth && p.hp <= 0 && !p.eliminated) return;
      const spot = FORMATIONS.kickoffA[Math.min(index, FORMATIONS.kickoffA.length - 1)];
      p.reset(new THREE.Vector3(spot.x, 0, spot.z), 1, restoreHealth);
    });
    teamB.forEach((p, index) => {
      if (!restoreHealth && p.hp <= 0 && !p.eliminated) return;
      const spot = FORMATIONS.kickoffB[Math.min(index, FORMATIONS.kickoffB.length - 1)];
      p.reset(new THREE.Vector3(spot.x, 0, spot.z), -1, restoreHealth);
    });
    this.director.setMode('broadcast');
    this.scorerHot = null;
    this.statusTimer = 0;
    this.hud.setStatus('¡Preparados!');
    this.hud.setScore(this.scoreA, this.scoreB);
  }

  private startMatch(silent = false): void {
    this.combatChain = 0;
    this.chainTimer = 0;
    this.combatHits = 0;
    this.lastAttack = null;
    this.hud.showTitle(false);
    this.trophy.visible = false;
    this.ball.group.visible = true;
    this.scoreA = 0;
    this.scoreB = 0;
    this.matchTime = 0;
    this.golden = false;
    this.pendingEnd = false;
    this.resetKickoff(true);
    this.explosiveBallTimer = 24 + this.rng() * 22;
    this.audio.startBackground();
    this.phase = 'countdown';
    this.phaseTimer = MATCH.kickoffCountdown;
    if (!silent) {
      this.audio.whistle('short');
      this.audio.bark('start');
    }
    this.hud.showBanner('3', 'SAQUE INICIAL', 'countdown');
  }

  private restartMatch(): void {
    this.hud.hideEnd();
    this.startMatch();
  }

  private startOnlineMatch(role: 'host' | 'guest' | 'spectator'): void {
    this.replicaBombs.forEach(bomb => { bomb.mesh.visible = false; });
    this.localPlayerCount = role === 'spectator' ? 0 : role === 'host' ? 2 : 1;
    this.humanPlayerIds[0] = role === 'guest' ? P2_ID : P1_ID;
    this.humanPlayerIds[1] = P2_ID;
    this.previousHumanSprint.fill(false);
    this.humanFocusGrace.fill(0);
    this.onlineSnapshot = null;
    this.networkAudio.length = 0;
    this.lastNetworkSend = 0;
    this.lastRemoteInput = 0;
    this.remoteIntent = { moveX: 0, moveZ: 0, sprint: false, shootHeld: false, shootPressed: false, shootReleased: false, passPressed: false, punchPressed: false, active: false };
    this.input.setLocalPlayerCount(role === 'spectator' ? 0 : 1);
    this.hud.setSpectator(role === 'spectator');
    this.getElement('#combat-hud').classList.toggle('hidden', role === 'spectator');
    this.updateControl();
    this.startMatch(role !== 'host');
  }

  private promoteOnlineMatch(state: OnlineSnapshot | null): void {
    this.localPlayerCount = 1;
    this.humanPlayerIds[0] = P2_ID;
    this.humanPlayerIds[1] = P2_ID;
    this.input.setLocalPlayerCount(1);
    this.hud.setSpectator(false);
    this.getElement('#combat-hud').classList.remove('hidden');
    this.replicaBombs.forEach(bomb => { bomb.mesh.visible = false; });
    if (state) this.adoptOnlineState(state);
    this.onlineSnapshot = null;
    this.updateControl();
    this.hud.setStatus('El creador se desconectó. Ahora eres anfitrión; la partida continúa contra la CPU.');
  }

  private adoptOnlineState(state: OnlineSnapshot): void {
    this.receiveOnlineSnapshot(state);
    for (const snapshot of state.players) this.players.find(player => player.id === snapshot.id)?.updateReplica(snapshot, 0, performance.now() / 1000, 0, true);
    this.weapons.applyNetworkState(state.weapons);
    this.rockets.applyNetworkState(state.rockets ?? []);
    this.ball.ownerId = state.ball.ownerId;
    this.ball.state = state.ball.state;
    this.ball.setExplosive(state.ball.explosive);
    this.ball.setPosition(new THREE.Vector3().fromArray(state.ball.position));
    this.ball.setVelocity(new THREE.Vector3().fromArray(state.ball.velocity));
  }

  private controlIntent(slot: 0 | 1): ControlIntent {
    if (slot === 1 && this.online.started && this.online.role === 'host') {
      if (performance.now() - this.lastRemoteInput > 500) {
        this.remoteIntent.moveX = this.remoteIntent.moveZ = 0;
        this.remoteIntent.sprint = this.remoteIntent.shootHeld = false;
        this.remoteIntent.punchPressed = this.remoteIntent.passPressed = this.remoteIntent.shootPressed = this.remoteIntent.shootReleased = false;
      }
      return this.remoteIntent;
    }
    return this.input.getIntent(slot);
  }

  private updateNetwork(): void {
    if (!this.online.started) return;
    if (this.online.role === 'spectator') return;
    if (this.online.role === 'guest') {
      this.online.sendInput(this.input.getIntent(0));
      return;
    }
    const now = performance.now();
    if (now - this.lastNetworkSend < 50 || this.phase === 'title') return;
    this.lastNetworkSend = now;
    const banner = this.getElement('#banner');
    const state: OnlineSnapshot = {
      phase: this.phase, time: this.matchTime, phaseTimer: this.phaseTimer, score: [this.scoreA, this.scoreB], golden: this.golden,
      focus: [...this.humanPlayerIds], players: this.players.map(player => player.networkState()), weapons: this.weapons.networkState(), rockets: this.rockets.networkState(),
      ball: { position: this.ball.getPosition(new THREE.Vector3()).toArray(), velocity: this.ball.getVelocity(new THREE.Vector3()).toArray(), ownerId: this.ball.ownerId, state: this.ball.state, explosive: this.ball.isExplosive },
      boxes: this.boxes.map(box => box.active),
      bombs: this.bombs.map(bomb => ({ position: bomb.mesh.position.toArray(), rotation: bomb.mesh.quaternion.toArray(), fuse: bomb.fuse })),
      banner: { main: this.getElement('#banner-main').textContent ?? '', sub: this.getElement('#banner-sub').textContent ?? '', tone: [...banner.classList].find(name => name.startsWith('tone-'))?.slice(5) ?? 'info', visible: !banner.classList.contains('hidden') },
      status: this.getElement('#status-line').textContent ?? '', audio: this.networkAudio.splice(0),
    };
    this.online.send({ type: 'state', state });
  }

  private receiveOnlineSnapshot(state: OnlineSnapshot): void {
    for (const snapshot of state.players) {
      const player = this.players.find(p => p.id === snapshot.id);
      if (!player) continue;
      if (!player.eliminated && snapshot.eliminated) {
        this.vfx.spawnExplosion(player.position);
        this.cameraShake(0.65);
      } else if (snapshot.hp < player.hp) {
        const origin = new THREE.Vector3().fromArray(snapshot.position).setY(0.8);
        const attacker = state.players.find(other => other.id[0] !== snapshot.id[0] && ['punch', 'weaponSwing', 'hammer', 'slide'].includes(other.state));
        if (attacker?.weaponType) this.vfx.weaponImpact(origin, attacker.weaponType, false);
        else this.vfx.combatImpact(origin, attacker?.attackStyle ?? 'punch');
        this.hud.impactFlash('hard');
      }
      if (snapshot.state === 'punch' && (player.state !== 'punch' || snapshot.stateTime < player.stateTime - 0.1)) {
        this.vfx.attackSweep(new THREE.Vector3().fromArray(snapshot.position), new THREE.Vector3().fromArray(snapshot.facing), snapshot.attackStyle);
      }
    }
    if (this.scoreA !== state.score[0] || this.scoreB !== state.score[1]) {
      this.vfx.spawnConfetti(new THREE.Vector3(0, 1.5, 0), 100);
      this.cameraShake(SCORING.goalTrauma);
    }
    this.scoreA = state.score[0];
    this.scoreB = state.score[1];
    this.matchTime = state.time;
    this.golden = state.golden;
    this.phase = state.phase;
    this.humanPlayerIds[0] = state.focus[this.online.role === 'spectator' ? 0 : 1];
    if (this.onlineSnapshot?.phase !== state.phase) this.audio.setPaused(state.phase === 'paused');
    for (const event of state.audio) {
      this.audio.playNetworkEvent(event.name, event.args);
      if (event.name === 'rocketExplosion') {
        this.vfx.rocketExplosion(new THREE.Vector3(event.args[0] as number, event.args[1] as number, event.args[2] as number));
        this.cameraShake(0.75);
      }
    }
    this.hud.showPause(state.phase === 'paused' && !this.options.isOpen);
    if (state.phase === 'complete' && this.onlineSnapshot?.phase !== 'complete') {
      this.hud.showEnd(this.scoreA > this.scoreB ? '¡GANA AZUL!' : this.scoreB > this.scoreA ? '¡GANA ROJO!' : '¡EMPATE!', `${this.scoreA} - ${this.scoreB}`, 'El creador puede iniciar la revancha.');
      this.audio.stopBackground();
      void this.audio.startEndMusic();
    } else if (state.phase !== 'complete') this.hud.hideEnd();
    this.hud.setScore(this.scoreA, this.scoreB);
    this.hud.setTimer(this.golden ? this.matchTime : Math.max(0, MATCH.duration - this.matchTime), this.golden);
    if (state.banner.visible) this.hud.showBanner(state.banner.main, state.banner.sub, state.banner.tone as BannerTone);
    else this.hud.hideBanner();
    this.hud.setStatus(`ONLINE · ${this.online.role === 'spectator' ? 'ESPECTADOR' : 'ROJO'} · ${state.status}`);
    this.onlineSnapshot = state;
    this.snapshotAge = 0;
  }

  private updateOnlineGuest(delta: number, elapsed: number): void {
    const snapshot = this.onlineSnapshot;
    if (!snapshot) return;
    const frozen = snapshot.phase === 'paused' || snapshot.phase === 'complete';
    this.snapshotAge = frozen ? 0 : Math.min(0.25, this.snapshotAge + delta);
    for (const state of snapshot.players) {
      const player = this.players.find(p => p.id === state.id)!;
      player.controlledBy = this.online.role !== 'spectator' && player.id === this.humanPlayerIds[0] ? 'human0' : 'ai';
      player.setHasBall(snapshot.ball.ownerId === player.id);
      player.updateReplica(state, delta, elapsed, this.snapshotAge);
      player.setRingVisible(player.controlledBy === 'human0');
    }
    this.weapons.applyNetworkState(snapshot.weapons);
    this.rockets.applyNetworkState(snapshot.rockets ?? [], this.snapshotAge);
    if (!frozen) this.rockets.update(delta, true);
    this.ball.ownerId = snapshot.ball.ownerId;
    this.ball.state = snapshot.ball.state;
    this.ball.setExplosive(snapshot.ball.explosive);
    this.ball.applyNetworkState(snapshot.ball, this.snapshotAge);
    this.ball.update(delta);
    this.boxes.forEach((box, index) => {
      box.active = snapshot.boxes[index];
      box.group.visible = box.active;
      if (box.active) box.update(0, elapsed, false);
    });
    snapshot.bombs.forEach((bomb, index) => {
      if (!this.replicaBombs[index]) {
        this.replicaBombs[index] = buildBombVisual();
        this.scene.add(this.replicaBombs[index].mesh);
      }
      const replica = this.replicaBombs[index];
      replica.mesh.visible = true;
      replica.mesh.position.fromArray(bomb.position);
      replica.mesh.quaternion.fromArray(bomb.rotation);
      replica.core.emissiveIntensity = Math.sin(bomb.fuse * 12) > 0 ? 2.2 : 0.1;
    });
    this.replicaBombs.forEach((bomb, index) => { if (index >= snapshot.bombs.length) bomb.mesh.visible = false; });
    const weapon = this.human0Player().weaponType;
    this.hud.setCombat(weapon ? `${WEAPONS[weapon].name} · PASE: ARROJAR` : ATTACKS[this.human0Player().nextAttackStyle].name, 0);
    this.passButton.textContent = weapon ? 'ARROJAR' : 'PASE';
    this.updateCamera(delta, elapsed);
    this.updateAmbience(delta, elapsed);
    this.vfx.update(delta);
  }

  private onGoal(team: 'A' | 'B'): void {
    if (team === 'A') this.scoreA += 1;
    else this.scoreB += 1;
    this.hud.setScore(this.scoreA, this.scoreB);
    this.hud.flashScore(team);
    const kicker = this.players.find((p) => p.id === this.ball.lastKickerId);
    const ownGoal = kicker !== undefined && kicker.team !== team;
    const sub = kicker
      ? ownGoal
        ? `EN PROPIA de ${kicker.name}`
        : `${kicker.name} · ${TEAM_LABELS[team]}`
      : TEAM_LABELS[team];
    this.hud.showBanner('¡GOOOL!', sub, 'goal');
    this.audio.goalHorn();
    this.audio.bark('goal');
    this.crowdExcite = 1;
    const goalX = team === 'A' ? COURT.halfLength : -COURT.halfLength;
    this.vfx.spawnConfetti(new THREE.Vector3(goalX * 0.7, 1.2, 0), 120);
    this.vfx.spawnConfetti(new THREE.Vector3(0, 1.5, 0), 70);
    this.cameraShake(SCORING.goalTrauma);
    this.director.punchFov(0.8);
    this.hud.impactFlash('hard');
    const hot = kicker ? kicker.position : this.ball.getPosition(new THREE.Vector3());
    this.scorerHot = new THREE.Vector3(hot.x, 0, hot.z);
    this.director.setMode('celebrate');
    this.netFlash = 1.4;
    for (const player of this.players) {
      player.cancelCharge();
      player.velocity.set(0, 0, 0);
      if (player.team === team && !player.isDown) {
        player.state = 'celebrate';
        player.stateTime = 0;
      }
    }
    this.pendingEnd = this.golden;
    this.phase = 'goal';
    this.phaseTimer = MATCH.goalCelebration;
    this.hud.setStatus(`¡Gol de ${TEAM_LABELS[team]}!`);
  }

  private finishMatch(): void {
    this.phase = 'complete';
    this.audio.stopBackground();
    void this.audio.startEndMusic();
    this.crowdExcite = 0.4;
    this.hud.hideBanner();
    const title =
      this.scoreA === this.scoreB
        ? '¡EMPATE!'
        : this.scoreA > this.scoreB
          ? '¡GANA AZUL!'
          : '¡GANA ROJO!';
    this.hud.showEnd(title, `${this.scoreA} - ${this.scoreB}`, 'ENTER o toque para la revancha');
    this.hud.setStatus('Fin del partido');
  }

  private checkMatchEnd(): void {
    if (this.golden) return;
    if (this.matchTime < MATCH.duration) return;
    if (this.scoreA === this.scoreB) {
      this.golden = true;
      this.hud.showBanner('¡GOL DE ORO!', 'EL PRÓXIMO GOL GANA', 'golden');
      this.bannerTimer = 3;
      this.statusTimer = 3;
    } else {
      this.finishMatch();
    }
  }

  private update(delta: number, elapsed: number): void {
    this.frame += 1;
    if (this.pausedForScreenshot) {
      if (import.meta.env.DEV) this.publishDiagnostics();
      return;
    }
    this.input.update();
    if (this.input.gamepadDisconnected && !this.options.isOpen && (this.phase === 'play' || this.phase === 'countdown')) {
      this.togglePause();
      this.hud.setStatus('Mando desconectado. Reconéctalo y pulsa Start para continuar.');
    }
    const optionsWereOpen = this.options.isOpen;
    const wasCapturing = this.options.isCapturingGamepad;
    this.options.updateGamepads();
    if (!wasCapturing) this.navigateGamepadMenu();
    if (optionsWereOpen || this.options.isOpen) {
      if (import.meta.env.DEV) this.publishDiagnostics();
      this.input.endFrame();
      return;
    }
    const animElapsed = this.reducedMotion ? 0 : elapsed;
    if (this.input.wasPressed('KeyM')) this.onSoundClick();
    if (this.input.wasPressed('KeyP') || this.input.wasPressed('Escape')) this.togglePause();
    if (this.online.started && this.online.role !== 'host') {
      this.updateOnlineGuest(delta, animElapsed);
      this.input.endFrame();
      if (import.meta.env.DEV) this.publishDiagnostics();
      return;
    }
    if (this.phase !== 'paused') {
      this.chainTimer = Math.max(0, this.chainTimer - delta);
      if (this.chainTimer === 0) this.combatChain = 0;
      this.statusTimer = Math.max(0, this.statusTimer - delta);
      this.bannerTimer = Math.max(0, this.bannerTimer - delta);
    }

    switch (this.phase) {
      case 'title':
        this.updateTitle(delta);
        break;
      case 'countdown': {
        this.phaseTimer -= delta;
        const count = Math.ceil(this.phaseTimer);
        if (this.phaseTimer <= 0) {
          this.phase = 'play';
          this.hud.showBanner('¡A JUGAR!', '', 'countdown');
          this.bannerTimer = 0.9;
          this.statusTimer = 0.9;
          this.audio.whistle('double');
          this.crowdExcite = Math.max(this.crowdExcite, 0.55);
        } else {
          this.hud.showBanner(String(count), 'SAQUE INICIAL', 'countdown');
        }
        this.simulateWorld(delta, animElapsed);
        break;
      }
      case 'play': {
        this.matchTime += delta;
        this.simulateGameplay(delta, animElapsed);
        this.checkMatchEnd();
        break;
      }
      case 'goal': {
        this.phaseTimer -= delta;
        this.simulateWorld(delta, animElapsed);
        if (this.phaseTimer <= 0) {
          if (this.pendingEnd) this.finishMatch();
          else {
            this.resetKickoff();
            this.phase = 'countdown';
            this.phaseTimer = MATCH.kickoffCountdown;
            this.hud.showBanner('3', 'SAQUE INICIAL', 'countdown');
          }
        }
        break;
      }
      case 'paused':
        if (import.meta.env.DEV) this.publishDiagnostics();
        this.input.endFrame();
        this.anyInput = false;
        return;
      case 'complete':
        this.simulateWorld(delta, animElapsed * 0.4);
        if (this.anyInput && !document.activeElement?.classList.contains('new-match')) this.restartMatch();
        break;
    }

    const heldWeapon = this.human0Player().weaponType;
    this.hud.setCombat(heldWeapon ? `${WEAPONS[heldWeapon].name} · J/PASE: ARROJAR` : ATTACKS[this.human0Player().nextAttackStyle].name, this.combatChain);
    const passLabel = heldWeapon ? 'ARROJAR' : 'PASE';
    if (this.passButton.textContent !== passLabel) this.passButton.textContent = passLabel;
    if (this.phase === 'play' && this.bannerTimer <= 0) this.hud.hideBanner();
    if (this.statusTimer <= 0 && this.phase === 'play') {
      const owner = this.players.find((p) => p.id === this.ball.ownerId);
      this.hud.setStatus(owner ? `Balón: ${owner.name} (${TEAM_LABELS[owner.team]})` : 'Balón libre');
    }

    this.updateCamera(delta, animElapsed);
    this.updateAmbience(delta, animElapsed);
    this.hud.setTimer(this.golden ? this.matchTime : Math.max(0, MATCH.duration - this.matchTime), this.golden);
    this.vfx.update(delta);
    if (import.meta.env.DEV) this.publishDiagnostics();
    this.input.endFrame();
    this.anyInput = false;
  }

  private updateTitle(delta: number): void {
    this.titleSway += delta * 0.4;
    this.trophy.rotation.y += delta * 0.5;
    this.trophy.position.y = Math.sin(this.titleSway * 2.4) * 0.04;
  }

  private navigateGamepadMenu(): void {
    const { x, y, accept, back, start } = this.input.menu;
    const onlineDialog = this.getElement('#online-dialog') as HTMLDialogElement;
    if (!x && !y && !accept && !back && !start) return;
    if (onlineDialog.open && back) {
      if (this.online.started) onlineDialog.close(); else this.online.leave();
      return;
    }
    if (this.options.isOpen && back) {
      this.getElement('#options-cancel').click();
      return;
    }
    if (!this.options.isOpen && (this.phase === 'play' || this.phase === 'countdown' || this.phase === 'paused') && (start || back && this.phase === 'paused')) {
      this.togglePause();
      return;
    }
    if (this.phase === 'complete' && !this.options.isOpen && document.activeElement?.classList.contains('new-match') && accept) {
      (document.activeElement as HTMLElement).click(); return;
    }
    if (this.phase === 'complete' && !this.options.isOpen && (accept || start)) {
      this.audio.menuClick();
      this.restartMatch();
      return;
    }
    if (this.phase === 'title' && !this.options.isOpen && !onlineDialog.open) {
      if (back) {
        this.audio.menuClick();
        if (!this.getElement('#menu-setup-content').classList.contains('hidden') && this.setupStage === 'sides') this.showSetupStage('count');
        else this.showMenuDetails(null);
        return;
      }
      if (start) {
        this.audio.menuClick();
        if (this.getElement('#menu-setup-content').classList.contains('hidden')) { this.options.useGamepads(); this.openMatchSetup(); }
        else if (this.setupStage === 'count') this.showSetupStage('sides');
        else this.confirmMatchSetup();
        return;
      }
    }
    const root = onlineDialog.open ? onlineDialog : this.options.isOpen ? ((this.getElement('#editor-dialog') as HTMLDialogElement).open ? this.getElement('#editor-dialog') : this.getElement('#options-dialog'))
      : this.phase === 'title' ? this.getElement('#screen-title')
      : this.phase === 'paused' ? this.getElement('#app') : this.phase === 'complete' ? this.getElement('#screen-end') : null;
    if (!root) return;
    const focusable = Array.from(root.querySelectorAll<HTMLElement>('button, a[href], summary, select, input, textarea'))
      .filter(element => element.getClientRects().length > 0 && !element.matches(':disabled') &&
        (this.options.isOpen || this.phase !== 'paused' || !!element.closest('#screen-pause, #match-controls')));
    if (!focusable.length) return;
    let selected = focusable.indexOf(document.activeElement as HTMLElement);
    if (selected < 0) selected = 0;
    else if (y) selected = (selected + y + focusable.length) % focusable.length;
    const element = focusable[selected];
    if (x && element instanceof HTMLSelectElement) {
      element.selectedIndex = Math.max(0, Math.min(element.options.length - 1, element.selectedIndex + x));
      element.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (x && element instanceof HTMLInputElement && element.type === 'number') {
      if (x > 0) element.stepUp(); else element.stepDown();
      element.dispatchEvent(new Event('input', { bubbles: true }));
    }
    document.querySelector('.gamepad-focus')?.classList.remove('gamepad-focus');
    element.classList.add('gamepad-focus');
    element.focus({ preventScroll: true });
    element.scrollIntoView({ block: 'nearest' });
    if (accept) {
      if (element.id === 'menu-quick-play') this.options.useGamepads();
      element.click();
    }
  }

  private simulateWorld(delta: number, animElapsed: number): void {
    for (const player of this.players) {
      player.intent = null;
      player.update(delta, animElapsed, false);
    }
    this.separatePlayers();
    this.physics.step(delta);
    this.ball.update(delta);
    this.updateBombs(delta);
    this.updateBoxes(delta, animElapsed);
    this.updateChargeAudio();
  }

  private simulateGameplay(delta: number, animElapsed: number): void {
    if (this.hitstop > 0) {
      // Keep impact emphasis to one tick instead of stalling the entire match for 75–160 ms.
      this.hitstop = 0;
      this.updateChargeAudio();
      return;
    }
    this.updateControl(delta);
    const context: AiContext = {
      ballPos: this.ball.getPosition(new THREE.Vector3()),
      ballVel: this.ball.getVelocity(new THREE.Vector3()),
      ballOwnerId: this.ball.ownerId,
      ballOwnerTeam: this.players.find((p) => p.id === this.ball.ownerId)?.team ?? null,
      active: !this.combatTest,
      findWeapon: this.weapons.nearestPickup,
    };
    this.ai.update(delta, context);
    for (const player of this.players) {
      player.setHasBall(this.ball.ownerId === player.id);
      if (player.controlledBy === 'human0') player.intent = { ...this.controlIntent(0), sprint: false };
      else if (player.controlledBy === 'human1') player.intent = { ...this.controlIntent(1), sprint: false };
      else player.intent = this.ai.getIntent(player.id);
    }
    this.routePossessionCommands();
    for (const player of this.players) {
      player.update(delta, animElapsed, true);
      player.setRingVisible(player.controlledBy === 'human0' || !this.online.started && player.controlledBy === 'human1');
    }
    this.separatePlayers();
    this.weapons.update(delta, animElapsed, !this.combatTest);
    this.rockets.update(delta);
    this.updatePossession(delta, context);
    this.resolveCombat();
    this.releasePendingThrows();
    this.updateBombs(delta);
    this.updateBoxes(delta, animElapsed);
    this.updateExplosiveBall(delta);
    this.updateChargeFx();
    this.physics.step(delta);
    this.ball.update(delta);
    this.checkGoal();
    this.spawnBallTrail();
    if (this.online.started && this.online.role === 'host') {
      this.remoteIntent.punchPressed = this.remoteIntent.passPressed = this.remoteIntent.shootPressed = this.remoteIntent.shootReleased = false;
    }
  }

  private updateControl(delta = 0): void {
    for (const slot of [0, 1] as const) {
      this.humanFocusGrace[slot] = Math.max(0, this.humanFocusGrace[slot] - delta);
      const input = this.controlIntent(slot);
      if (slot < this.localPlayerCount && input.sprint && !this.previousHumanSprint[slot]) {
        const teammates = this.players.filter(player => player.team === this.player(this.humanPlayerIds[slot]).team &&
          !player.eliminated &&
          !player.isDown &&
          !this.humanPlayerIds.some((id, other) => other !== slot && other < this.localPlayerCount && id === player.id));
        const currentIndex = teammates.findIndex(player => player.id === this.humanPlayerIds[slot]);
        if (teammates.length > 1) {
          this.humanPlayerIds[slot] = teammates[(currentIndex + 1) % teammates.length].id;
          this.humanFocusGrace[slot] = MANUAL_FOCUS_GRACE;
        }
      }
      this.previousHumanSprint[slot] = input.sprint;
    }
    if (this.phase === 'play' && !this.combatTest) this.updateAutomaticFocus();
    for (const player of this.players) {
      player.controlledBy = this.localPlayerCount > 0 && player.id === this.humanPlayerIds[0] ? 'human0'
        : this.localPlayerCount > 1 && player.id === this.humanPlayerIds[1] ? 'human1' : 'ai';
    }
  }

  private updateAutomaticFocus(): void {
    const ball = this.ball.getPosition(this.focusBallScratch);
    for (const slot of [0, 1] as const) {
      if (slot >= this.localPlayerCount) continue;
      const current = this.player(this.humanPlayerIds[slot]);
      if (this.humanFocusGrace[slot] > 0 && !current.isDown && !current.eliminated) continue;
      let nearest: PlayerEntity | undefined;
      let nearestDistance = Infinity;
      let currentDistance = Infinity;
      for (const candidate of this.players) {
        if (candidate.team !== current.team || candidate.eliminated || candidate.isDown ||
          this.humanPlayerIds.some((id, other) => other !== slot && other < this.localPlayerCount && id === candidate.id)) continue;
        const distance = Math.hypot(candidate.position.x - ball.x, candidate.position.z - ball.z);
        if (candidate.id === current.id) currentDistance = distance;
        if (distance < nearestDistance) {
          nearest = candidate;
          nearestDistance = distance;
        }
      }
      if (nearest && (currentDistance === Infinity || nearestDistance + AUTO_FOCUS_MARGIN < currentDistance)) {
        this.humanPlayerIds[slot] = nearest.id;
      }
    }
  }

  private routePossessionCommands(): void {
    const owner = this.players.find((player) => player.id === this.ball.ownerId);
    if (!owner) return;
    for (const slot of [0, 1] as const) {
      if (slot >= this.localPlayerCount) continue;
      const controlled = this.player(this.humanPlayerIds[slot]);
      const input = this.controlIntent(slot);
      if (controlled.isDown) continue;
      if (owner.id === controlled.id && owner.isGoalkeeper && owner.state === 'gkCatch' && (input.passPressed || input.shootPressed)) {
        owner.state = 'gkThrow';
        owner.stateTime = 0;
      }
      if (owner.team !== controlled.team || owner.id === controlled.id) continue;
      if (input.passPressed && owner.canAct) {
        owner.cancelCharge();
        owner.state = 'pass';
        owner.stateTime = 0;
        owner.kickDelivered = true;
        this.doPass(owner, controlled);
      } else if (input.shootPressed) {
        const attackDirection = new THREE.Vector3(owner.team === 'A' ? 1 : -1, 0, 0);
        owner.forceKick(attackDirection, 1, true);
      }
      if (controlled.intent && (input.passPressed || input.shootPressed || input.shootHeld || input.shootReleased)) {
        controlled.intent = { ...controlled.intent, passPressed: false, shootHeld: false, shootPressed: false, shootReleased: false };
      }
    }
  }

  private separatePlayers(): void {
    for (let i = 0; i < this.players.length; i += 1) {
      const a = this.players[i];
      if (a.eliminated) continue;
      for (let j = i + 1; j < this.players.length; j += 1) {
        const b = this.players[j];
        if (b.eliminated) continue;
        const dx = b.position.x - a.position.x;
        const dz = b.position.z - a.position.z;
        const distance = Math.hypot(dx, dz);
        const minDistance = PLAYER.radius * 1.84;
        if (distance > 1e-4 && distance < minDistance) {
          const push = (minDistance - distance) * 0.5;
          const nx = dx / distance;
          const nz = dz / distance;
          const aDown = a.isDown;
          const bDown = b.isDown;
          if (!aDown) {
            a.position.x -= nx * push * (bDown ? 2 : 1);
            a.position.z -= nz * push * (bDown ? 2 : 1);
          }
          if (!bDown) {
            b.position.x += nx * push * (aDown ? 2 : 1);
            b.position.z += nz * push * (aDown ? 2 : 1);
          }
        }
      }
      for (const box of this.boxes) {
        if (!box.active) continue;
        const dx = a.position.x - box.home.x;
        const dz = a.position.z - box.home.z;
        const distance = Math.hypot(dx, dz);
        const minDistance = this.crateRadius + PLAYER.radius;
        if (distance > 1e-4 && distance < minDistance) {
          const push = minDistance - distance;
          a.position.x += (dx / distance) * push;
          a.position.z += (dz / distance) * push;
        }
      }
      const margin = PLAYER.radius + 0.05;
      a.position.x = THREE.MathUtils.clamp(a.position.x, -14.56 + margin, 14.56 - margin);
      a.position.z = THREE.MathUtils.clamp(a.position.z, -8.6 + margin, 8.6 - margin);
    }
  }

  private updatePossession(delta: number, context: AiContext): void {
    const ballPos = context.ballPos;
    const goalkeepers = this.players.filter((p) => p.isGoalkeeper);

    if (this.ball.state === 'held') {
      const gk = this.players.find((p) => p.id === this.ball.ownerId);
      if (!gk || (gk.state !== 'gkCatch' && gk.state !== 'gkThrow')) {
        this.ball.state = 'free';
        this.ball.ownerId = null;
      } else if (!(gk.state === 'gkThrow' && this.gkThrown.has(gk.id))) {
        const hand = this.separationScratch.set(
          gk.position.x + gk.facing.x * 0.42,
          0.95,
          gk.position.z + gk.facing.z * 0.42,
        );
        this.ball.setPosition(hand);
        this.ball.freeze();
        return;
      }
    }

    for (const gk of goalkeepers) {
      if (this.ball.ownerId === gk.id) continue;
      if (this.ball.pickupLockId === gk.id) continue;
      const side = gk.team === 'A' ? -1 : 1;
      const goalX = side * COURT.halfLength;
      if (Math.abs(goalX - gk.position.x) > GOALKEEPER_AREA.depth || Math.abs(gk.position.z) > GOALKEEPER_AREA.halfWidth) continue;
      const canCatch =
        gk.state === 'gkGuard' || gk.state === 'gkDive' || gk.state === 'idle' || gk.state === 'run';
      if (!canCatch) continue;
      const dx = ballPos.x - gk.position.x;
      const dz = ballPos.z - gk.position.z;
      const distance = Math.hypot(dx, dz);
      if (distance < 0.95 && ballPos.y < 1.85) {
        const speed = this.ball.speed();
        if (speed > 20.5) {
          const dir = new THREE.Vector3(dx, 0, dz).normalize();
          if (dir.lengthSq() < 0.5) dir.set(gk.team === 'A' ? 1 : -1, 0, 0);
          dir.x = Math.sign(dir.x) * Math.abs(dir.x);
          dir.multiplyScalar(9);
          dir.x += (gk.team === 'A' ? 1 : -1) * 5;
          dir.y = 4.2;
          this.ball.setVelocity(dir);
          this.ball.state = 'shot';
          this.audio.post();
          this.vfx.spawnImpact(this.separationScratch.set(ballPos.x, ballPos.y, ballPos.z), '#fff1c9', 16);
          this.cameraShake(SCORING.postTrauma);
          this.setStatus(`¡Manotazo de ${gk.name}!`);
        } else {
          gk.startCatchHold();
          this.ball.state = 'held';
          this.ball.ownerId = gk.id;
          this.ball.freeze();
          this.audio.bounce(0.6);
          this.cameraShake(0.16);
          this.setStatus(`¡ATAJADA de ${gk.name}!`);
        }
        return;
      }
    }

    const owner = this.players.find((p) => p.id === this.ball.ownerId);
    if (owner) {
      if (owner.isDown) {
        this.ball.state = 'free';
        this.ball.ownerId = null;
        return;
      }
      const dx = ballPos.x - owner.position.x;
      const dz = ballPos.z - owner.position.z;
      if (Math.hypot(dx, dz) > 1.45) {
        this.ball.state = 'free';
        this.ball.ownerId = null;
      } else {
        const magnet = magnetActive(owner);
        for (const opponent of this.players) {
          if (opponent.team === owner.team || !opponent.canAct) continue;
          const ox = ballPos.x - opponent.position.x;
          const oz = ballPos.z - opponent.position.z;
          const stealRange = magnet ? PLAYER.touchRadius * 0.5 : PLAYER.touchRadius;
          if (Math.hypot(ox, oz) < stealRange) {
            this.ball.ownerId = opponent.id;
            this.audio.punchWhoosh();
            this.vfx.spawnDust(new THREE.Vector3(ballPos.x, 0.1, ballPos.z), 6);
            this.setStatus(`${opponent.name} robó el balón`);
            return;
          }
        }
        const target = this.separationScratch.set(
          owner.position.x + owner.facing.x * 0.62 + owner.velocity.x * 0.05,
          BALL.radius + 0.02,
          owner.position.z + owner.facing.z * 0.62 + owner.velocity.z * 0.05,
        );
        this.ball.possess(target, delta, magnet ? 15 : 11.5);
        return;
      }
    }

    if (this.ball.pickupLockTimer <= 0 && this.ball.state !== 'shot') this.ball.state = 'free';
    if (this.ball.state === 'shot' && this.ball.speed() < 6.5) this.ball.state = 'free';

    let best: PlayerEntity | null = null;
    let bestDistance = Infinity;
    for (const player of this.players) {
      if (!player.canAct || player.state === 'slide') continue;
      if (this.ball.pickupLockId === player.id) continue;
      const dx = ballPos.x - player.position.x;
      const dz = ballPos.z - player.position.z;
      const distance = Math.hypot(dx, dz);
      if (distance < PLAYER.controlRadius && distance < bestDistance) {
        best = player;
        bestDistance = distance;
      }
    }
    if (best) {
      this.ball.ownerId = best.id;
      this.ball.state = 'free';
      this.ball.lastKickerId = best.id;
      return;
    }

    for (const player of this.players) {
      if (!player.canAct || !magnetActive(player)) continue;
      const dx = ballPos.x - player.position.x;
      const dz = ballPos.z - player.position.z;
      const distance = Math.hypot(dx, dz);
      if (distance < ITEMS.magnetRadius && distance > 0.85) {
        this.ball.possess(
          this.separationScratch.set(player.position.x, BALL.radius + 0.02, player.position.z),
          delta,
          8.5,
        );
        break;
      }
    }
  }

  private resolveCombat(): void {
    for (const attacker of this.players) {
      const window = attacker.hitWindow;
      if (!window) continue;
      for (const target of this.players) {
        if (target.team === attacker.team) continue;
        if (target.isDown || target.state === 'celebrate') {
          continue;
        }
        const dx = target.position.x - attacker.position.x;
        const dz = target.position.z - attacker.position.z;
        const distance = Math.hypot(dx, dz);
        const range =
          window.type === 'weapon'
            ? attacker.weaponType ? WEAPONS[attacker.weaponType].range : 0
            : window.type === 'punch'
            ? ATTACKS[attacker.attackStyle].range
            : window.type === 'hammer'
              ? ITEMS.hammerRange
              : 1.05;
        if (distance > range || distance < 1e-4) continue;
        const nx = dx / distance;
        const nz = dz / distance;
        const dot = nx * window.dirX + nz * window.dirZ;
        const radial = window.type === 'punch' && (attacker.attackStyle === 'spin' || attacker.attackStyle === 'slam');
        if (!radial && dot < (window.type === 'slide' ? -0.35 : 0.15)) continue;
        attacker.hitLanded = true;
        if (window.type === 'weapon') this.weapons.hitWith(attacker, target, new THREE.Vector3(nx, 0, nz));
        else this.applyHit(attacker, target, window.type, new THREE.Vector3(nx, 0, nz));
        if (!radial) break;
      }
    }
  }

  private applyHit(
    attacker: PlayerEntity,
    target: PlayerEntity,
    type: 'punch' | 'slide' | 'hammer',
    dir: THREE.Vector3,
  ): void {
    const impact = new THREE.Vector3(target.position.x, 1.05, target.position.z);
    const ground = new THREE.Vector3(target.position.x, 0, target.position.z);
    if (type !== 'punch') this.vfx.spawnHitLabel(impact);
    const dropBall = () => {
      this.weapons.dropFrom(target);
      if (this.ball.ownerId === target.id) {
        this.ball.ownerId = null;
        this.ball.state = 'free';
        this.ball.pickupLockId = target.id;
        this.ball.pickupLockTimer = 0.4;
        this.ball.setVelocity(new THREE.Vector3(dir.x * 4.5, 4.6, dir.z * 4.5));
      }
    };
    if (type === 'punch') {
      const style = attacker.attackStyle;
      const attack = ATTACKS[style];
      this.audio.combatImpact(style);
      this.vfx.combatImpact(impact, style);
      const ko = target.applyDamage(getAttackDamage(style), false, attacker);
      if (!target.eliminated) this.audio.reactToHit(style === 'uppercut' ? 'uppercut' : style === 'slam' ? 'hitVoice' : 'punchVoice');
      target.startDown(ko ? 1.65 : 1.05, dir, attack.force, true, attack.reaction, attack.lift);
      this.hitstop = Math.max(this.hitstop, style === 'slam' || ko ? 0.13 : 0.075);
      this.crowdExcite = Math.max(this.crowdExcite, 0.7);
      this.lastAttack = style;
      this.combatHits += 1;
      if (attacker.controlledBy === 'human0') {
        this.combatChain += 1;
        this.chainTimer = 4;
        this.director.showImpact(target.position);
        this.director.punchFov(style === 'uppercut' || style === 'slam' ? 0.85 : 0.55);
        this.cameraShake(0.48);
        this.hud.impactFlash(ko || style === 'slam' ? 'hard' : 'soft');
      } else this.cameraShake(0.18);
      this.setStatus(`${attack.name} · ${attacker.name} ${ko ? 'borró del mapa a' : 'mandó a volar a'} ${target.name}`);
      dropBall();
    } else if (type === 'hammer') {
      this.audio.punchHit();
      this.audio.down();
      this.vfx.spawnImpact(impact, '#ffd23f', 30);
      this.vfx.spawnImpact(impact, '#ffffff', 14);
      this.vfx.spawnDust(ground, 20);
      this.vfx.shockwave(ground, '#ffd23f', 4.2, 0.38);
      this.cameraShake(0.78);
      this.director.punchFov(0.7);
      this.hitstop = Math.max(this.hitstop, 0.13);
      this.crowdExcite = Math.max(this.crowdExcite, 0.5);
      this.hud.impactFlash('hard');
      target.applyDamage(getSpecialDamage('hammer'), false, attacker);
      if (!target.eliminated) this.audio.reactToHit();
      target.startDown(1.5, dir, ITEMS.hammerKnockback, true, 'flatten', 10);
      dropBall();
      this.setStatus(`¡MARTILLAZO SÍSMICO de ${attacker.name}! ${target.name} voló por los aires`);
    } else {
      this.audio.punchHit();
      this.audio.slide();
      this.vfx.spawnDust(ground, 22);
      this.vfx.spawnImpact(impact, '#ffd9ad', 12);
      this.vfx.shockwave(ground, '#ffb37a', 3, 0.32);
      this.cameraShake(SCORING.tackleTrauma);
      this.director.punchFov(0.5);
      this.hitstop = Math.max(this.hitstop, SCORING.hitstopTackle / 1000);
      this.crowdExcite = Math.max(this.crowdExcite, 0.4);
      const staminaBefore = target.stamina;
      const ko = target.applyDamage(getSpecialDamage('slide'), false, attacker);
      if (!target.eliminated) this.audio.reactToHit();
      if (ko || staminaBefore < 30) {
        this.audio.down();
        this.cameraShake(0.7);
        this.vfx.spawnDust(ground, 18);
        this.hud.impactFlash('hard');
        target.startDown(PLAYER.slideKnockdown, dir, 18, true, 'spin', 9);
        this.setStatus(`¡PATADA VOLADORA! ${attacker.name} demolió a ${target.name}`);
      } else {
        this.hud.impactFlash('soft');
        target.startHurt(dir, 10.5);
        this.setStatus(`${attacker.name} barrió a ${target.name}`);
      }
      dropBall();
    }
  }

  private fireBazooka(attacker: PlayerEntity): void {
    if (!this.rockets.canLaunch || !this.weapons.consumeShot(attacker)) return;
    this.rockets.launch(attacker);
    attacker.hitLanded = true;
    this.audio.rocketLaunch();
    this.cameraShake(0.2);
    this.setStatus(`¡MISIL! ${attacker.name} dispara la bazooka · ¡CUIDADO CON LOS ALIADOS!`);
  }

  private explodeRocket(position: THREE.Vector3, direct: PlayerEntity | undefined, ownerId?: string): void {
    const origin = position.clone();
    const attacker = this.players.find(player => player.id === ownerId);
    // A direct hit eliminates regardless of HP. Splash also reaches downed players and the shooter.
    direct?.eliminate(attacker);
    for (const player of this.players) {
      if (player.eliminated || player.hp <= 0 || player === direct || player.state === 'celebrate') continue;
      const direction = player.position.clone().setY(player.airborneY + 0.85).sub(origin);
      const distance = direction.length();
      if (distance > ROCKET.radius) continue;
      const falloff = 1 - distance / ROCKET.radius;
      direction.setY(0);
      if (direction.lengthSq() < 0.0001) direction.copy(player.facing).negate();
      direction.normalize();
      player.applyDamage(getWeaponDamage('bazooka') * (0.3 + 0.7 * falloff), true, attacker);
      player.startDown(1.7, direction, 12 + 18 * falloff, true, 'spin', 9 + 7 * falloff);
      this.weapons.dropFrom(player);
      if (this.ball.ownerId === player.id) { this.ball.ownerId = null; this.ball.state = 'free'; }
    }
    // Emit after elimination sounds so the blast itself has its own editable sound.
    this.audio.rocketExplosion(origin.x, origin.y, origin.z);
    this.vfx.rocketExplosion(origin);
    this.hud.impactFlash('hard');
    this.cameraShake(0.8);
    this.hitstop = Math.max(this.hitstop, 0.08);
    this.crowdExcite = 1;
    this.combatHits++;
    this.setStatus(direct ? `¡KABOOM! ${direct.name} recibió un misil` : '¡KABOOM! Explosión de bazooka');
  }

  private fireShotgun(attacker: PlayerEntity): void {
    if (!this.weapons.consumeShot(attacker)) return;
    this.audio.shotgun();
    const muzzle = attacker.position.clone().addScaledVector(attacker.facing, 0.8).setY(1.1 + attacker.airborneY);
    this.vfx.spawnImpact(muzzle, '#fff09a', 18);
    this.cameraShake(0.25);
    let target: PlayerEntity | undefined;
    let nearest: number = WEAPONS.shotgun.range;
    for (const rival of this.players) {
      if (rival.team === attacker.team || rival.eliminated || rival.hp <= 0 || rival.state === 'celebrate') continue;
      const dx = rival.position.x - attacker.position.x;
      const dz = rival.position.z - attacker.position.z;
      const distance = Math.hypot(dx, dz);
      const along = dx * attacker.facing.x + dz * attacker.facing.z;
      const across = Math.abs(dx * attacker.facing.z - dz * attacker.facing.x);
      if (along <= 0 || distance > nearest || across > 0.6 + along * 0.04 || Math.abs(rival.airborneY - attacker.airborneY) > 1.5) continue;
      nearest = distance;
      target = rival;
    }
    attacker.hitLanded = true;
    if (!target) { this.setStatus(`¡BOOM! ${attacker.name} dispara la escopeta`); return; }
    this.vfx.weaponImpact(target.position.clone().setY(1), 'shotgun', false);
    if (nearest <= SHOTGUN_KILL_RANGE) target.eliminate(attacker);
    else {
      target.applyDamage(getWeaponDamage('shotgun'), false, attacker);
      if (!target.eliminated) this.audio.reactToHit('weaponVoice');
      target.startDown(1.1, attacker.facing, WEAPONS.shotgun.force, true, 'spin', WEAPONS.shotgun.lift);
    }
    this.weapons.dropFrom(target);
    if (this.ball.ownerId === target.id) {
      this.ball.ownerId = null;
      this.ball.state = 'free';
      this.ball.setVelocity(attacker.facing.clone().multiplyScalar(5).setY(3));
    }
    this.combatHits++;
    this.setStatus(`${nearest <= SHOTGUN_KILL_RANGE ? '¡A QUEMARROPA!' : '¡ESCOPETAZO!'} ${attacker.name} → ${target.name}`);
  }

  private applyWeaponHit(attacker: PlayerEntity, target: PlayerEntity, type: WeaponType, direction: THREE.Vector3, broken: boolean, thrown: boolean): void {
    const weapon = WEAPONS[type];
    target.applyDamage(getWeaponDamage(type), false, attacker);
    target.startDown(type === 'warhammer' ? 1.8 : 1.2, direction, weapon.force, true, type === 'warhammer' ? 'launch' : type === 'plank' ? 'flatten' : 'spin', weapon.lift);
    this.weapons.dropFrom(target);
    if (this.ball.ownerId === target.id) {
      this.ball.ownerId = null;
      this.ball.state = 'free';
      this.ball.pickupLockId = target.id;
      this.ball.pickupLockTimer = 0.4;
      this.ball.setVelocity(direction.clone().multiplyScalar(5).setY(4));
    }
    this.audio.weaponImpact(type, broken);
    if (!target.eliminated) this.audio.reactToHit('weaponVoice');
    this.vfx.weaponImpact(target.position.clone().setY(1), type, broken);
    this.hitstop = Math.max(this.hitstop, isHeavyWeapon(type) ? 0.16 : 0.1);
    this.crowdExcite = Math.max(this.crowdExcite, 0.75);
    this.combatHits += 1;
    if (attacker.controlledBy === 'human0') {
      this.combatChain += 1;
      this.chainTimer = 4;
      this.director.showImpact(target.position);
      this.cameraShake(isHeavyWeapon(type) ? 0.7 : 0.48);
      this.hud.impactFlash('soft');
    }
    this.setStatus(`${thrown ? '¡PROYECTIL!' : '¡ARMAZAZO!'} ${weapon.name} · ${attacker.name} → ${target.name}${broken ? ' · ¡SE ROMPIÓ!' : ''}`);
  }

  private doKick(owner: PlayerEntity, direction: THREE.Vector3, charge: number, aimAtGoal = false): void {
    charge = 1;
    const ballPos = this.ball.getPosition(new THREE.Vector3());
    if (this.ball.ownerId !== owner.id) {
      const dx = ballPos.x - owner.position.x;
      const dz = ballPos.z - owner.position.z;
      if (Math.hypot(dx, dz) > 1.5) return;
    }
    if (this.detonateExplosiveBall(owner)) return;
    const attackSign = owner.team === 'A' ? 1 : -1;
    const goalX = attackSign * COURT.halfLength;
    const facingZ = aimAtGoal ? 0 : THREE.MathUtils.clamp(
      ballPos.z + direction.z * 4.5,
      -GOAL.halfWidth + 0.25,
      GOAL.halfWidth - 0.25,
    );
    let targetZ = facingZ;
    const keeper = this.players.find((p) => p.isGoalkeeper && p.team !== owner.team);
    if (keeper && Math.abs(keeper.position.z - facingZ) < 1.1) {
      const cornerSign = keeper.position.z > 0 ? -1 : 1;
      targetZ = cornerSign * (GOAL.halfWidth - 0.3);
    }
    const toGoal = new THREE.Vector3(goalX - ballPos.x, 0, targetZ - ballPos.z).normalize();
    const flat = new THREE.Vector3(direction.x, 0, direction.z).normalize();
    const finalDir = aimAtGoal ? toGoal : flat.lerp(toGoal, 0.78).normalize();
    const speed = PLAYER.shotMinSpeed + charge * (PLAYER.shotMaxSpeed - PLAYER.shotMinSpeed);
    const loft = 0.04 + charge * 0.09;
    this.ball.powerShot = charge > 0.85;
    this.ball.kick(finalDir, speed, loft, owner.id);
    this.audio.kick(charge);
    this.audio.bark('kickVoice');
    this.vfx.spawnImpact(new THREE.Vector3(ballPos.x, 0.24, ballPos.z), charge > 0.7 ? '#ffd23f' : '#fff1c9', charge > 0.7 ? 20 : 10);
    this.cameraShake(SCORING.kickTrauma + charge * 0.1);
    this.director.punchFov(charge * 0.5);
    this.crowdExcite = Math.max(this.crowdExcite, 0.3 + charge * 0.35);
    this.setStatus(
      charge > 0.8
        ? `¡TIRO POTENTE de ${owner.name}!`
        : charge > 0.4
          ? `Tiro de ${owner.name}`
          : `${owner.name} dispara`,
    );
  }

  private doPass(owner: PlayerEntity, preferredTarget?: PlayerEntity): void {
    if (preferredTarget?.isDown) preferredTarget = undefined;
    const ballPos = this.ball.getPosition(new THREE.Vector3());
    let target: PlayerEntity | null = preferredTarget ?? (owner.controlledBy === 'ai' ? this.ai.getPassTarget(owner) : null);
    let bestScore = -Infinity;
    for (const mate of target ? [] : this.players) {
      if (mate.team !== owner.team || mate.id === owner.id || mate.isDown) continue;
      let openness = 14;
      for (const opponent of this.players) {
        if (opponent.team === owner.team || opponent.isDown) continue;
        const dx = mate.position.x - opponent.position.x;
        const dz = mate.position.z - opponent.position.z;
        openness = Math.min(openness, Math.hypot(dx, dz));
      }
      const attackSign = owner.team === 'A' ? 1 : -1;
      const forward = mate.isGoalkeeper ? -6 : (mate.position.x - owner.position.x) * attackSign;
      const score = openness + forward * 0.7;
      if (score > bestScore) {
        bestScore = score;
        target = mate;
      }
    }
    const aim = target
      ? new THREE.Vector3(
          target.position.x + target.velocity.x * 0.35,
          0,
          target.position.z + target.velocity.z * 0.35,
        )
      : new THREE.Vector3(owner.facing.x, 0, owner.facing.z);
    const dir = new THREE.Vector3(aim.x - ballPos.x, 0, aim.z - ballPos.z);
    if (dir.lengthSq() < 1e-4) dir.set(owner.facing.x, 0, owner.facing.z);
    dir.normalize();
    if (this.detonateExplosiveBall(owner)) return;
    this.ball.kick(dir, PLAYER.passSpeed, 0.02, owner.id);
    this.audio.pass();
    this.vfx.spawnImpact(new THREE.Vector3(ballPos.x, 0.24, ballPos.z), '#9fd6ff', 6);
    this.setStatus(`Pase de ${owner.name}`);
  }

  private useItem(player: PlayerEntity): void {
    const type = player.item;
    if (!type) return;
    player.setItem(null);
    if (type === 'magnet') {
      player.magnetTimer = ITEMS.magnetDuration;
      this.audio.pickup();
      this.vfx.spawnBurst(new THREE.Vector3(player.position.x, 1, player.position.z), '#ff5349', 20, { speed: 3 });
      this.setStatus(`¡Guantes imán de ${player.name}!`);
    } else if (type === 'bomb') {
      this.bombPending.add(player.id);
      this.audio.punchWhoosh();
    } else {
      this.audio.punchWhoosh();
      this.setStatus(`${player.name} empuña el martillo`);
    }
  }

  private releasePendingThrows(): void {
    for (const id of [...this.bombPending]) {
      const player = this.players.find((p) => p.id === id);
      if (!player || player.state !== 'bomb') {
        this.bombPending.delete(id);
        continue;
      }
      if (player.stateTime > 0.3) {
        this.bombPending.delete(id);
        const origin = new THREE.Vector3(
          player.position.x + player.facing.x * 0.55,
          1.15,
          player.position.z + player.facing.z * 0.55,
        );
        const velocity = new THREE.Vector3(player.facing.x * 13.5, 3.6, player.facing.z * 13.5);
        const body = this.physics.createBomb(origin, velocity);
        if (!body) continue;
        const { mesh, core } = buildBombVisual();
        this.scene.add(mesh);
        this.bombs.push({ body, mesh, core, fuse: ITEMS.bombFuse, ownerId: player.id });
        this.setStatus(`${player.name} lanzó una BOMBA`);
      }
    }

    for (const gk of this.players) {
      if (!gk.isGoalkeeper) continue;
      if (gk.state !== 'gkThrow') {
        this.gkThrown.delete(gk.id);
        continue;
      }
      if (!this.gkThrown.has(gk.id) && gk.stateTime > 0.28) {
        this.gkThrown.add(gk.id);
        let target: PlayerEntity | null = null;
        let best = -Infinity;
        for (const mate of this.players) {
          if (mate.team !== gk.team || mate.isGoalkeeper) continue;
          let openness = 12;
          for (const opponent of this.players) {
            if (opponent.team === gk.team) continue;
            const dx = mate.position.x - opponent.position.x;
            const dz = mate.position.z - opponent.position.z;
            openness = Math.min(openness, Math.hypot(dx, dz));
          }
          const attackSign = gk.team === 'A' ? 1 : -1;
          const score = openness + (mate.position.x - gk.position.x) * attackSign * 0.6;
          if (score > best) {
            best = score;
            target = mate;
          }
        }
        const ballPos = this.ball.getPosition(new THREE.Vector3());
        if (this.detonateExplosiveBall(gk)) continue;
        const aim = target
          ? new THREE.Vector3(target.position.x, 0, target.position.z)
          : new THREE.Vector3(gk.team === 'A' ? 6 : -6, 0, 0);
        const dir = new THREE.Vector3(aim.x - ballPos.x, 0, aim.z - ballPos.z).normalize();
        this.ball.state = 'shot';
        this.ball.ownerId = null;
        this.ball.pickupLockId = gk.id;
        this.ball.pickupLockTimer = 0.55;
        this.ball.setVelocity(new THREE.Vector3(dir.x * 15, 4.6, dir.z * 15));
        this.audio.pass();
        this.setStatus(`${gk.name} juega rápido`);
      }
    }
  }

  private updateBombs(delta: number): void {
    for (let i = this.bombs.length - 1; i >= 0; i -= 1) {
      const bomb = this.bombs[i];
      const t = bomb.body.translation();
      bomb.mesh.position.set(t.x, t.y, t.z);
      const r = bomb.body.rotation();
      bomb.mesh.quaternion.set(r.x, r.y, r.z, r.w);
      bomb.fuse -= delta;
      const blinkRate = bomb.fuse < 0.5 ? 34 : 12;
      bomb.core.emissiveIntensity = Math.sin(bomb.fuse * blinkRate) > 0 ? 2.2 : 0.1;
      if (bomb.fuse <= 0) {
        this.explodeBomb(bomb);
        this.bombs.splice(i, 1);
      }
    }
  }

  private explodeBomb(bomb: Bomb): void {
    const t = bomb.body.translation();
    const origin = new THREE.Vector3(t.x, Math.max(0.2, t.y), t.z);
    this.physics.removeBody(bomb.body);
    this.scene.remove(bomb.mesh);
    this.explodeAt(origin, '¡BOOM!', this.players.find(player => player.id === bomb.ownerId));
  }

  private detonateExplosiveBall(kicker: PlayerEntity): boolean {
    if (!this.ball.isExplosive) return false;
    const origin = this.ball.getPosition(new THREE.Vector3()).setY(0.3);
    this.ball.setExplosive(false);
    kicker.eliminate();
    this.explodeAt(origin, '¡BALÓN TRAMPA! ¡BOOM!');
    return true;
  }

  private explodeAt(origin: THREE.Vector3, status = '¡BOOM!', attacker?: PlayerEntity): void {
    this.audio.bombExplosion();
    this.vfx.spawnExplosion(origin);
    this.cameraShake(SCORING.bombTrauma);
    this.crowdExcite = Math.max(this.crowdExcite, 0.85);
    this.hitstop = Math.max(this.hitstop, SCORING.hitstopBomb / 1000);
    for (const player of this.players) {
      const dx = player.position.x - origin.x;
      const dz = player.position.z - origin.z;
      const distance = Math.hypot(dx, dz);
      if (distance > ITEMS.bombRadius || distance < 1e-4) continue;
      const falloff = 1 - distance / ITEMS.bombRadius;
      const dir = new THREE.Vector3(dx / distance, 0, dz / distance);
      if (player.isDown || player.eliminated) continue;
      player.applyDamage(getSpecialDamage('bomb') * (0.55 + falloff * 0.45), false, attacker);
      if (!player.eliminated) this.audio.reactToHit();
      player.startDown(ITEMS.bombKnockdown, dir, 12 + falloff * 12, true, 'spin', 9 + falloff * 5);
      this.weapons.dropFrom(player);
      if (this.ball.ownerId === player.id) {
        this.ball.ownerId = null;
        this.ball.state = 'free';
      }
    }
    const ballPos = this.ball.getPosition(new THREE.Vector3());
    const bdx = ballPos.x - origin.x;
    const bdz = ballPos.z - origin.z;
    const bdistance = Math.hypot(bdx, bdz);
    if (bdistance < ITEMS.bombRadius * 1.25 && bdistance > 1e-4) {
      const falloff = 1 - bdistance / (ITEMS.bombRadius * 1.25);
      this.ball.setVelocity(
        new THREE.Vector3((bdx / bdistance) * 16 * falloff, 5.5 * falloff + 2, (bdz / bdistance) * 16 * falloff),
      );
      this.ball.state = 'shot';
      this.ball.ownerId = null;
    }
    this.setStatus(status);
  }

  private updateExplosiveBall(delta: number): void {
    if (this.phase !== 'play' || this.combatTest || this.ball.isExplosive) return;
    this.explosiveBallTimer -= delta;
    if (this.explosiveBallTimer > 0) return;
    this.ball.setExplosive(true);
    this.explosiveBallTimer = 24 + this.rng() * 22;
    this.vfx.spawnImpact(this.ball.getPosition(new THREE.Vector3()).setY(0.35), '#ff3b16', 22);
    this.setStatus('¡BALÓN EXPLOSIVO! ¡PÁTALO BAJO TU PROPIO RIESGO!');
  }

  private updateBoxes(delta: number, animElapsed: number): void {
    const activeCount = this.boxes.filter((box) => box.active).length;
    for (const box of this.boxes) {
      box.update(delta, animElapsed, activeCount < ITEMS.maxBoxes);
    }
    if (this.phase !== 'play') return;
    for (const box of this.boxes) {
      if (!box.active) continue;
      for (const player of this.players) {
        if (!player.canAct) continue;
        const dx = player.position.x - box.home.x;
        const dz = player.position.z - box.home.z;
        if (Math.hypot(dx, dz) < 0.88) {
          box.collect();
          const roll = this.rng();
          const item: ItemType = roll < 0.38 ? 'bomb' : roll < 0.62 ? 'hammer' : 'magnet';
          player.setItem(item, 0);
          this.audio.pickup();
          this.vfx.spawnImpact(new THREE.Vector3(box.home.x, 0.5, box.home.z), '#ffd23f', 18);
          const label = item === 'bomb' ? 'una BOMBA' : item === 'hammer' ? 'el MARTILLO' : 'los GUANTES IMÁN';
          this.setStatus(`${player.name} tomó ${label}`);
          break;
        }
      }
    }
  }

  private checkGoal(): void {
    if (this.phase !== 'play') return;
    const ballPos = this.ball.getPosition(new THREE.Vector3());
    if (Math.abs(ballPos.z) > GOAL.halfWidth + 0.05) return;
    if (ballPos.y > GOAL.height + 0.12) return;
    if (ballPos.x > COURT.halfLength + 0.18) this.onGoal('A');
    else if (ballPos.x < -COURT.halfLength - 0.18) this.onGoal('B');
  }

  private spawnBallTrail(): void {
    const speed = this.ball.speed();
    if (speed < 16) return;
    this.trailTimer -= 0.016;
    if (this.trailTimer > 0) return;
    this.trailTimer = 0.024;
    const pos = this.ball.getPosition(new THREE.Vector3());
    const kicker = this.players.find((p) => p.id === this.ball.lastKickerId);
    const color = kicker ? TEAM_COLORS[kicker.team] : '#ffe9a3';
    this.vfx.spawnTrail(new THREE.Vector3(pos.x, pos.y + 0.05, pos.z), color);
  }

  private updateChargeAudio(): void {
    const charger = this.players.find((p) => p.charging);
    if (!charger) {
      if (this.chargeAudioId) {
        this.audio.chargeStop();
        this.chargeAudioId = null;
      }
      return;
    }
    if (this.chargeAudioId !== charger.id) {
      this.audio.chargeStop();
      this.audio.chargeStart();
      this.chargeAudioId = charger.id;
    }
    this.audio.chargeUpdate(charger.charge);
  }

  private updateChargeFx(): void {
    const charger = this.players.find((p) => p.charging);
    if (charger) {
      const pos = this.ball.getPosition(new THREE.Vector3());
      this.vfx.setChargeRing(pos, charger.charge, TEAM_COLORS[charger.team]);
    } else {
      this.vfx.setChargeRing(null, 0, '#ffffff');
    }
  }

  private onBallContact(tag: string, speed: number): void {
    if (speed < 2.5) return;
    if (tag === 'post' || tag === 'crossbar') {
      this.audio.post();
      const pos = this.ball.getPosition(new THREE.Vector3());
      this.vfx.spawnImpact(new THREE.Vector3(pos.x, pos.y, pos.z), '#fff1c9', 14);
      this.cameraShake(SCORING.postTrauma);
    } else if (tag === 'board') {
      this.audio.bounce(speed / 18);
      this.cameraShake(SCORING.wallTrauma * Math.min(1, speed / 14));
    } else {
      this.audio.bounce(speed / 30);
      this.netFlash = Math.max(this.netFlash, Math.min(0.28, speed / 100));
    }
  }

  private cameraShake(amount: number): void {
    this.director.addTrauma(amount);
  }

  private setStatus(text: string): void {
    this.hud.setStatus(text);
    this.statusTimer = 2.2;
  }

  private updateCamera(delta: number, animElapsed: number): void {
    const portrait = this.isPortrait();
    if (this.phase === 'title') {
      const focus = new THREE.Vector3(Math.sin(this.titleSway * 0.6) * 3.4, 0, 0);
      this.director.update(delta, focus, new THREE.Vector3(0, 0, 0), null, portrait, this.camera.aspect);
      return;
    }
    void animElapsed;
    const ballPos = this.ball.getPosition(new THREE.Vector3());
    const ballVel = this.ball.getVelocity(new THREE.Vector3());
    this.director.update(delta, ballPos, ballVel, this.scorerHot, portrait, this.camera.aspect);
  }

  private updateAmbience(delta: number, animElapsed: number): void {
    this.crowdExcite = Math.max(0, this.crowdExcite - delta * 0.35);
    this.stadium.crowd.update(animElapsed, this.crowdExcite);
    if (this.netFlash > 0) {
      this.netFlash = Math.max(0, this.netFlash - delta);
      const intensity = Math.min(1, this.netFlash);
      for (const material of this.stadium.goalNetMaterials) {
        material.emissive.set('#ffffff');
        material.emissiveIntensity = intensity * 0.8;
      }
    }
    resizeRenderer(this.renderer, this.camera, this.tuning.maxDpr);
  }

  private render(delta = 0): void {
    this.stadium.updateAdvertising(performance.now() / 1000);
    if (!document.hidden) {
      if (delta > 0 && delta <= 0.25) this.adaptRenderResolution(delta);
      else this.qualityWindow = this.qualityFrames = 0;
      this.updateFps();
    } else {
      this.qualityWindow = this.qualityFrames = this.fpsFrames = 0;
      this.fpsSampleTime = performance.now();
    }
    // shortcut: one pass wins on the GTX 1650; restore a prepass if fill-rate-bound targets regress.
    this.renderer.render(this.scene, this.camera);
  }

  private installTestHooks(): void {
    if (!import.meta.env.DEV) return;
    window.__THREE_GAME_TEST_HOOKS__ = {
      seed: (value: number) => {
        this.rng = createSeededRandom(value);
      },
      setState: (name: string) => {
        const injuryTest = ['injury-light', 'injury-heavy', 'injury-critical', 'injury-crawl', 'injury-ko'].includes(name);
        const weaponType = name.startsWith('weapon-') ? name.split('-').at(-1) as WeaponType : undefined;
        const weaponTest = weaponType !== undefined && WEAPON_TYPES.includes(weaponType);
        if (name !== 'combat' && name !== 'recovery' && !weaponTest && !injuryTest) this.combatTest = false;
        if (name === 'active-play') {
          this.hud.showTitle(false);
          this.hud.hideEnd();
          this.hud.showPause(false);
          this.hud.hideBanner();
          this.trophy.visible = false;
          this.scoreA = 0;
          this.scoreB = 0;
          this.matchTime = 0;
          this.golden = false;
          this.pendingEnd = false;
          this.resetKickoff(true);
          this.phase = 'play';
          this.hud.setStatus('¡A jugar!');
        } else if (name === 'combat' || name === 'recovery' || weaponTest || injuryTest) {
          this.combatTest = true;
          this.hud.showTitle(false);
          this.hud.hideBanner();
          this.trophy.visible = false;
          this.phase = 'play';
          this.weapons.clear();
          this.rockets.clear();
          this.hitstop = 0;
          const attacker = this.human0Player();
          if (weaponTest || injuryTest || name === 'recovery') attacker.reset(new THREE.Vector3(-0.7, 0, 0), 1);
          if (name === 'recovery') attacker.applyDamage(40);
          attacker.position.set(-0.7, 0, 0);
          attacker.velocity.set(0, 0, 0);
          attacker.facing.set(1, 0, 0);
          for (const player of this.players) {
            if (player === attacker) continue;
            const target = player.id === P2_ID;
            player.reset(new THREE.Vector3(target ? name.startsWith('weapon-throw-') ? 4.5 : 0.7 : player.team === 'A' ? -11 : 11, 0, target ? 0 : player.isGoalkeeper ? 0 : 5), -1);
            if (target && injuryTest) player.applyDamage(name === 'injury-light' ? 20 : name === 'injury-heavy' ? 50 : name === 'injury-critical' ? 75 : name === 'injury-crawl' ? 90 : 82);
          }
          if (weaponTest) this.weapons.spawn(weaponType, attacker.position);
          this.ball.reset(new THREE.Vector3(-3, BALL.radius + 0.02, 3));
          this.director.snapTo(new THREE.Vector3(0, 0, 0), this.isPortrait());
        } else if (name === 'goal') {
          if (this.phase !== 'play') this.phase = 'play';
          this.onGoal('A');
        } else if (name === 'kickoff') {
          this.resetKickoff();
          this.phase = 'countdown';
          this.phaseTimer = MATCH.kickoffCountdown;
          this.hud.showBanner('3', 'SAQUE INICIAL', 'countdown');
        } else if (name === 'golden') {
          this.golden = true;
          this.phase = 'play';
          this.hud.showBanner('¡GOL DE ORO!', 'EL PRÓXIMO GOL GANA', 'golden');
          this.bannerTimer = 3;
          this.statusTimer = 3;
        } else if (name === 'regulation-end') {
          this.phase = 'play';
          this.matchTime = MATCH.duration;
          this.scoreA = this.scoreB = 0;
          this.checkMatchEnd();
        } else if (name === 'complete') {
          this.finishMatch();
        } else {
          console.warn(`Unknown test state: ${name}`);
        }
      },
      setPausedForScreenshot: (paused: boolean) => {
        this.pausedForScreenshot = paused;
      },
      setReducedMotion: (enabled: boolean) => {
        this.reducedMotion = enabled;
        this.director.setReducedMotion(enabled);
      },
      hideDebugUi: (hidden: boolean) => {
        this.debugTools.setHidden(hidden);
      },
      setFocusScenario: (ball, positions) => {
        this.phase = 'play';
        this.combatTest = false;
        this.ball.ownerId = null;
        this.ball.setPosition(new THREE.Vector3(ball.x, BALL.radius + 0.02, ball.z));
        this.ball.freeze();
        for (const { id, x, z } of positions) {
          const player = this.player(id);
          player.position.set(x, 0, z);
          player.velocity.set(0, 0, 0);
        }
        this.humanFocusGrace.fill(0);
        this.updateControl();
        return this.humanPlayerIds.slice(0, this.localPlayerCount);
      },
    };
  }

  private nearestOpponentDistance(player: PlayerEntity): number {
    let best = Infinity;
    for (const other of this.players) {
      if (other.team === player.team) continue;
      const distance = other.position.distanceTo(player.position);
      if (distance < best) best = distance;
    }
    return Number.isFinite(best) ? Number(best.toFixed(2)) : 99;
  }

  private publishDiagnostics(): void {
    const info = this.renderer.info;
    const controlled = this.human0Player();
    const owner = this.players.find((p) => p.id === this.ball.ownerId);
    const ballPos = this.ball.getPosition(new THREE.Vector3());
    const ballVel = this.ball.getVelocity(new THREE.Vector3());
    window.__THREE_GAME_DIAGNOSTICS__ = {
      frame: this.frame,
      elapsed: this.matchTime,
      score: this.scoreA,
      targetScore: this.scoreB,
      complete: this.phase === 'complete',
      phase: this.phase,
      localPlayerCount: this.localPlayerCount,
      timer: this.golden ? this.matchTime : Math.max(0, MATCH.duration - this.matchTime),
      golden: this.golden,
      weapons: this.weapons.diagnostics,
      rockets: this.rockets.networkState(),
      combat: { hits: this.combatHits, chain: this.combatChain, lastAttack: this.lastAttack, nextAttack: this.human0Player().nextAttackStyle, activeLabels: this.vfx.activeHitLabels },
      standings: { a: this.scoreA, b: this.scoreB },
      player: {
        id: controlled.id,
        team: controlled.team,
        position: {
          x: controlled.position.x,
          y: controlled.airborneY,
          z: controlled.position.z,
        },
        speed: controlled.speed,
        stamina: controlled.stamina,
        hp: controlled.hp,
        state: controlled.state,
        hasBall: this.ball.ownerId === controlled.id,
        item: controlled.item,
        weapon: controlled.weaponType,
        nearestOpponent: this.nearestOpponentDistance(controlled),
      },
      ball: {
        position: { x: ballPos.x, y: ballPos.y, z: ballPos.z },
        velocity: { x: ballVel.x, y: ballVel.y, z: ballVel.z },
        speed: Math.hypot(ballVel.x, ballVel.y, ballVel.z),
        ownerId: this.ball.ownerId,
        state: this.ball.state,
        explosive: this.ball.isExplosive,
        ownerState: owner?.state ?? null,
        ownerStateTime: owner ? Number(owner.stateTime.toFixed(2)) : null,
      },
      squad: this.players.map((p) => ({
        id: p.id,
        team: p.team,
        x: Number(p.position.x.toFixed(2)),
        z: Number(p.position.z.toFixed(2)),
        y: Number(p.airborneY.toFixed(2)),
        reaction: p.hitReaction,
        attackStyle: p.attackStyle,
        state: p.state,
        controlledBy: p.controlledBy,
        hp: p.hp,
        eliminated: p.eliminated,
        injury: p.injurySeverity,
        mobility: p.mobilityFactor,
      })),
      renderer: {
        calls: info.render.calls,
        triangles: info.render.triangles,
        points: info.render.points,
        geometries: info.memory.geometries,
        textures: info.memory.textures,
      },
      canvas: {
        clientWidth: this.canvas.clientWidth,
        clientHeight: this.canvas.clientHeight,
        width: this.canvas.width,
        height: this.canvas.height,
        dpr: Math.min(window.devicePixelRatio || 1, this.tuning.maxDpr),
      },
    };
  }

  private getElement(selector: string): HTMLElement {
    const element = document.querySelector<HTMLElement>(selector);
    if (!element) throw new Error(`Missing element: ${selector}`);
    return element;
  }
}
